/**
 * Pokemon Showdown Tooltips
 *
 * A file for generating tooltips for battles. This should be IE7+ and
 * use the DOM directly.
 *
 * @author Guangcong Luo <guangcongluo@gmail.com>
 * @license MIT
 */

class ModifiableValue {
	value = 0;
	maxValue = 0;
 secondHitPower?: number;
 dualWieldPower = false;
 dualWieldHit = 1;
 dualWieldFull = false;
 dualWieldBoosted = false;
 dualWieldPotentialPower = 1;
 dualWieldPotentialDamage = false;
	rangeSeparator = ' to ';
	comment: string[];
	battle: Battle;
	pokemon: Pokemon;
	serverPokemon: ServerPokemon;
	itemName: string;
	abilityName: string;
	weatherName: string;
	isAccuracy = false;
	constructor(battle: Battle, pokemon: Pokemon, serverPokemon: ServerPokemon) {
		this.comment = [];
		this.battle = battle;
		this.pokemon = pokemon;
		this.serverPokemon = serverPokemon;

		this.itemName = Dex.items.get(serverPokemon.item).name;
		const ability = serverPokemon.ability || pokemon?.ability || serverPokemon.baseAbility;
		this.abilityName = Dex.abilities.get(ability).name;
		this.weatherName = this.weatherDisplayName(this.effectiveWeather());
	}
	reset(value = 0, isAccuracy?: boolean) {
		this.value = value;
		this.maxValue = 0;
  this.secondHitPower = undefined;
	 this.dualWieldPower = false;
	 this.dualWieldBoosted = false;
	 this.dualWieldPotentialPower = 1;
	 this.dualWieldPotentialDamage = false;
		this.rangeSeparator = ' to ';
		this.isAccuracy = !!isAccuracy;
		this.comment = [];
	}
	tryItem(itemName: string) {
		if (itemName !== this.itemName) return false;
		if (this.battle.hasPseudoWeather('Magic Room')) {
			this.comment.push(` (${itemName} suppressed by Magic Room)`);
			return false;
		}
		if (this.pokemon?.volatiles['embargo']) {
			this.comment.push(` (${itemName} suppressed by Embargo)`);
			return false;
		}
		const ignoreKlutz = [
			"Macho Brace", "Power Anklet", "Power Band", "Power Belt", "Power Bracer", "Power Lens", "Power Weight",
		];
		if (this.tryAbility('Klutz') && !ignoreKlutz.includes(itemName)) {
			this.comment.push(` (${itemName} suppressed by Klutz)`);
			return false;
		}
		return true;
	}
	tryAbility(abilityName: string) {
		const passives = this.serverPokemon.passives ?? this.battle.dex.species.get(this.serverPokemon.speciesForme).passives;
		if (passives.some(passive => Dex.getPassiveAbilityEffects(toID(passive)).has(toID(abilityName)))) return true;
		const activeId = Dex.abilities.get(this.abilityName).id;
		const excluded = Dex.getAbilityComponentExclusions(activeId, passives, toID(this.serverPokemon.speciesForme));
		const hasComponent = ((this.dualWieldPower || ['perfectform', 'titansknuckles', 'blazemastery', 'plushwhiplash', 'tidalpoise', 'abyssalclaws', 'antlioninstinct', 'tidalvoice', 'apexvenom', 'sirius', 'abyssalfang', 'trickperformer', 'curtaincall', 'cellularconcord', 'mountainhide', 'rimebark', 'bottomlessgullet', 'divefisher', 'scatterbrain'].includes(activeId)) || (activeId === 'quarrycannon' && abilityName === 'Mega Launcher') || (['updraft', 'stormsovereign'].includes(activeId) && abilityName === 'Competitive') || ['Guts', 'Rivalry', 'Pure Power', 'Sharpness', 'Dual Wield', 'Power Drill', 'Mega Launcher', 'Fire Mane'].includes(abilityName) || (['atrocity', 'apexpredator'].includes(activeId) && abilityName === 'Tough Claws') || (activeId === 'rupturedheaven' && abilityName === 'Sharpness') || (activeId === 'wintersmalice' && abilityName === 'Refrigerate')) && Dex.getAbilityEffects(activeId).has(toID(abilityName)) && !excluded.includes(toID(abilityName));
		if (!hasComponent && Dex.abilities.get(abilityName).id !== activeId &&
			!(this.abilityName === 'Cinder Scales' && abilityName === 'Swarm')) return false;
		if (this.pokemon?.volatiles['gastroacid']) {
			this.comment.push(` (${abilityName} suppressed by Gastro Acid)`);
			return false;
		}
		// Check for Neutralizing Gas
		if (this.pokemon && !this.pokemon.effectiveAbility(this.serverPokemon)) return false;
		return true;
	}
	weatherDisplayName(weather: ID) {
		return !weather ? '' : weather === 'snow' ? 'Snow' : Dex.moves.get(weather).exists ?
			Dex.moves.get(weather).name : Dex.abilities.get(weather).name;
	}
	effectiveWeather(): ID {
		// Known active components suppress weather; suppressed selected abilities do not.
		for (const active of this.battle.getAllActive()) {
			if (active.fainted) continue;
			const passives = active === this.pokemon ?
				(this.serverPokemon.passives ?? active.getSpecies().passives) : active.getSpecies().passives;
			const ability = toID(active.effectiveAbility(active === this.pokemon ? this.serverPokemon : undefined));
			const excluded = Dex.getAbilityComponentExclusions(ability, passives, toID(active.getSpeciesForme()));
			const effects = new Set<ID>();
			for (const effect of Array.from(Dex.getAbilityEffects(ability))) if (!excluded.includes(effect)) effects.add(effect);
			for (const passive of passives) for (const effect of Array.from(Dex.getPassiveAbilityEffects(toID(passive)))) effects.add(effect);
			if (effects.has('airlock' as ID) || effects.has('cloudnine' as ID)) return '' as ID;
		}
		const umbrella = this.tryItem('Utility Umbrella');
		if (!umbrella && this.tryAbility('Solar Mimicry')) return 'sunnyday' as ID;
		const weather = this.battle.weather;
		if (umbrella && ['sunnyday', 'desolateland', 'raindance', 'primordialsea'].includes(weather)) return '' as ID;
		return weather;
	}
	tryWeather(weatherName?: string) {
		const current = this.weatherDisplayName(this.effectiveWeather());
		return !!current && (!weatherName || weatherName === current);
	}
	itemModify(factor: number, itemName?: string) {
		if (!itemName) itemName = this.itemName;
		if (!itemName) return false;
		if (!this.tryItem(itemName)) return false;
		return this.modify(factor, itemName);
	}
	abilityModify(factor: number, abilityName: string) {
		if (!this.tryAbility(abilityName)) return false;
		return this.powerAbilityModify(factor, abilityName);
	}
	weatherModify(factor: number, weatherName?: string, name?: string) {
		if (!weatherName) weatherName = this.weatherName;
		if (!weatherName) return false;
		if (!this.tryWeather(weatherName)) return false;
		return name && Dex.abilities.get(name).exists && this.tryAbility(name) ?
			this.powerAbilityModify(factor, name) : this.modify(factor, name || weatherName);
	}
 powerAbilityModify(factor: number, name: string) {
	if (this.dualWieldPower && factor > 1) {
		this.dualWieldBoosted = true;
		if (this.dualWieldHit > 1 && !this.dualWieldFull) {
			this.comment.push(' (' + name + ' boost applies to first hit)');
			return true;
		}
	}
	return this.modify(factor, name);
 }
	modify(factor: number, name?: string) {
		if (factor === 0) {
			if (name) this.comment.push(` (${name})`);
			this.value = 0;
			this.maxValue = 0;
			return true;
		}
		if (name) this.comment.push(` (${this.round(factor)}&times; from ${name})`);
		this.value *= factor;
		if (!(name === 'Technician' && this.maxValue > this.technicianThreshold())) this.maxValue *= factor;
		return true;
	}
	set(value: number, reason?: string) {
		if (reason) this.comment.push(` (${reason})`);
		this.value = value;
		this.maxValue = 0;
		return true;
	}
	technicianThreshold() {
		return this.battle.pseudoWeather.some(([field]) => this.battle.getTerrainId(field) === 'factoryterrain') ? 80 : 60;
	}
	setRange(value: number, maxValue: number, reason?: string) {
		if (reason) this.comment.push(` (${reason})`);
		this.value = value;
		this.maxValue = maxValue;
		return true;
	}
	round(value: number) {
		return value ? Number(value.toFixed(2)) : 0;
	}
	toString() {
		let valueString;
		if (this.isAccuracy) {
			valueString = this.value ? `${this.round(this.value)}%` : `can't miss`;
		} else {
			valueString = this.value ? `${this.round(this.value)}` : ``;
		}
		if (this.maxValue) {
			valueString += `${this.rangeSeparator}${this.round(this.maxValue)}` + (this.isAccuracy ? '%' : '');
		}
		return valueString + this.comment.join('');
	}
}

const STEEL_MOVE_POWER_ABILITIES: {[abilityName: string]: number} = {
	'Razor Current': 1.5,
};

class BattleTooltips {
	static getTypeAbilityWeakness(type: TypeName, ability: ID, dex: ModdedDex = Dex): number {
		const effects = Dex.getAbilityEffects(ability, new Set<ID>(), dex);
		const has = (id: string) => effects.has(toID(id));
		if (type === 'Ground' && has('levitate')) return 0;
		if (type === 'Water' && ['waterabsorb', 'stormdrain', 'dryskin'].some(has)) return 0;
		if (type === 'Fire' && ['flashfire', 'wellbakedbody'].some(has)) return 0;
		if (type === 'Electric' && ['voltabsorb', 'motordrive', 'lightningrod'].some(has)) return 0;
		if (type === 'Grass' && has('sapsipper')) return 0;
		let factor = 1;
		if ((type === 'Fire' || type === 'Ice') && has('thickfat')) factor *= 0.5;
		if (type === 'Fire' && (has('waterbubble') || has('seafiend'))) factor *= 0.5;
		if (type === 'Fire' && has('heatproof')) factor *= 0.5;
		if (type === 'Fire' && has('dryskin')) factor *= 1.25;
		if (type === 'Ghost' && has('purifyingsalt')) factor *= 0.5;
		return factor;
	}
	battle: Battle;

	constructor(battle: Battle) {
		this.battle = battle;
	}

	// tooltips
	// Touch delay, pressing finger more than that time will cause the tooltip to open.
	// Shorter time will cause the button to click
	static LONG_TAP_DELAY = 350; // ms
	static longTapTimeout = 0;
	static elem: HTMLDivElement | null = null;
	static parentElem: HTMLElement | null = null;
	static isLocked = false;
	static isPressed = false;

	static hideTooltip() {
		if (!BattleTooltips.elem) return;
		BattleTooltips.cancelLongTap();
		BattleTooltips.elem.parentNode!.removeChild(BattleTooltips.elem);
		BattleTooltips.elem = null;
		BattleTooltips.parentElem = null;
		BattleTooltips.isLocked = false;
		$('#tooltipwrapper').removeClass('tooltip-locked');
	}

	static cancelLongTap() {
		if (BattleTooltips.longTapTimeout) {
			clearTimeout(BattleTooltips.longTapTimeout);
			BattleTooltips.longTapTimeout = 0;
		}
	}

	lockTooltip() {
		if (BattleTooltips.elem && !BattleTooltips.isLocked) {
			BattleTooltips.isLocked = true;
			if (BattleTooltips.isPressed) {
				$(BattleTooltips.parentElem!).removeClass('pressed');
				BattleTooltips.isPressed = false;
			}
			$('#tooltipwrapper').addClass('tooltip-locked');
		}
	}

	handleTouchEnd(e: TouchEvent) {
		BattleTooltips.cancelLongTap();

		if (!BattleTooltips.isLocked) BattleTooltips.hideTooltip();
	}

	listen(elem: HTMLElement | JQuery<HTMLElement>) {
		const $elem = $(elem);
		const battleControls = $elem.hasClass('battle-controls');
		const touchGenerated = (e: JQuery.TriggeredEvent) => battleControls && (
			(e.originalEvent as MouseEvent & {sourceCapabilities?: {firesTouchEvents: boolean}})?.sourceCapabilities?.firesTouchEvents ||
			window.matchMedia('(hover: none)').matches
		);
		$elem.on('mouseover', '.has-tooltip', e => { if (!touchGenerated(e)) this.showTooltipEvent(e); });
		$elem.on('click', '.has-tooltip', this.clickTooltipEvent);
		$elem.on('focus', '.has-tooltip', e => {
			if (!touchGenerated(e) || e.currentTarget.matches(':focus-visible')) this.showTooltipEvent(e);
		});
		$elem.on('mouseout', '.has-tooltip', BattleTooltips.unshowTooltip);
		$elem.on('mousedown', '.has-tooltip', e => { if (!touchGenerated(e)) this.holdLockTooltipEvent(e); });
		$elem.on('blur', '.has-tooltip', BattleTooltips.unshowTooltip);
		$elem.on('mouseup', '.has-tooltip', BattleTooltips.unshowTooltip);

		if (battleControls) {
			// Let the browser distinguish a tap from a scroll. Only a deliberate hold opens details.
			$elem.on('touchstart', '.has-tooltip', e => {
				BattleTooltips.hideTooltip();
				BattleTooltips.cancelLongTap();
				const target = e.currentTarget as HTMLElement;
				BattleTooltips.longTapTimeout = setTimeout(() => {
					BattleTooltips.longTapTimeout = 0;
					if (!target.isConnected) return;
					this.showTooltip(target);
					this.lockTooltip();
				}, BattleTooltips.LONG_TAP_DELAY);
			});
			$elem.on('touchmove touchcancel', '.has-tooltip', BattleTooltips.cancelLongTap);
			$elem.on('touchend', '.has-tooltip', e => {
				BattleTooltips.cancelLongTap();
				if (BattleTooltips.isLocked) e.preventDefault();
			});
			return;
		}
		$elem.on('touchstart', '.has-tooltip', e => {
			e.preventDefault();
			this.holdLockTooltipEvent(e);
			if (!BattleTooltips.parentElem) {
				// should never happen, but in case there's a bug in the tooltip handler
				BattleTooltips.parentElem = e.currentTarget;
			}
			$(BattleTooltips.parentElem!).addClass('pressed');
			BattleTooltips.isPressed = true;
		});
		$elem.on('touchend', '.has-tooltip', e => {
			e.preventDefault();
			if (e.currentTarget === BattleTooltips.parentElem && BattleTooltips.isPressed) {
				BattleTooltips.parentElem!.click();
			}
			BattleTooltips.unshowTooltip();
		});
		$elem.on('touchleave', '.has-tooltip', BattleTooltips.unshowTooltip);
		$elem.on('touchcancel', '.has-tooltip', BattleTooltips.unshowTooltip);
	}

	clickTooltipEvent = (e: Event) => {
		const badge = e.currentTarget as HTMLElement;
		if (badge.classList.contains('relic-armor-badge')) {
			e.preventDefault();
			this.showTooltip(badge);
			this.lockTooltip();
			return;
		}
		if (BattleTooltips.isLocked) {
			e.preventDefault();
			e.stopImmediatePropagation();
		}
	};
	/**
	 * An event that will lock a tooltip if held down
	 *
	 * (Namely, a long-tap or long-click)
	 */
	holdLockTooltipEvent = (e: JQuery.TriggeredEvent) => {
		if (BattleTooltips.isLocked) BattleTooltips.hideTooltip();
		const target = e.currentTarget as HTMLElement;
		this.showTooltip(target);
		let factor = (e.type === 'mousedown' && target.tagName === 'BUTTON' ? 2 : 1);

		BattleTooltips.longTapTimeout = setTimeout(() => {
			BattleTooltips.longTapTimeout = 0;
			this.lockTooltip();
		}, BattleTooltips.LONG_TAP_DELAY * factor);
	};

	showTooltipEvent = (e: JQuery.TriggeredEvent) => {
		if (BattleTooltips.isLocked) return;
		this.showTooltip(e.currentTarget as HTMLElement);
	};

	/**
	 * Only hides tooltips if they're not locked
	 */
	static unshowTooltip() {
		if (BattleTooltips.isLocked) return;
		if (BattleTooltips.isPressed) {
			$(BattleTooltips.parentElem!).removeClass('pressed');
			BattleTooltips.isPressed = false;
		}
		BattleTooltips.hideTooltip();
	}

	showTooltip(elem: HTMLElement) {
		const args = (elem.dataset.tooltip || '').split('|');
		const [type] = args;
		/**
		 * If false, we instead attach the tooltip above the parent element.
		 * This is important for the move/switch menus so the tooltip doesn't
		 * cover up buttons above the hovered button.
		 */
		let ownHeight = !!elem.dataset.ownheight;

		let buf: string;
		switch (type) {
		case 'relicarmor': {
			buf = '<div class="tooltip"><h2>Relic Armor</h2><p>10% less attack damage.</p><p>' + (args[1] === 'field' ? 'Field protection active: Rock typing adds no Fighting, Ground, Steel, Water or Grass weakness.' : 'Field protection inactive: Rock-typing weaknesses remain.') + '</p></div>';
			break;
		}
		case 'move':
		case 'zmove':
		case 'maxmove': { // move|MOVE|ACTIVEPOKEMON|[GMAXMOVE]
			let move = this.battle.dex.moves.get(args[1]);
			let teamIndex = parseInt(args[2], 10);
			let pokemon = this.battle.nearSide.active[
				teamIndex + this.battle.pokemonControlled * Math.floor(this.battle.mySide.n / 2)
			];
			let gmaxMove = args[3] ? this.battle.dex.moves.get(args[3]) : undefined;
			if (!pokemon) return false;
			let serverPokemon = this.battle.myPokemon![teamIndex];
			if (type === 'move' && args[4] === 'sevipermega') {
				// A selection preview must never mutate the live Pokemon or request.
				const species = this.battle.dex.species.get('Seviper-Mega');
				serverPokemon = {...serverPokemon, stats: serverPokemon.megaStats || serverPokemon.stats, speciesForme: species.name, ability: 'Sirius',
					baseAbility: 'Sirius', passives: species.passives};
				pokemon = Object.assign(Object.create(Object.getPrototypeOf(pokemon)), pokemon,
					{speciesForme: species.name, ability: 'Sirius', baseAbility: 'Sirius'}) as Pokemon;
			}
			buf = this.showMoveTooltip(move, type, pokemon, serverPokemon, gmaxMove);
			break;
		}

		case 'pokemon': { // pokemon|SIDE|POKEMON
			// mouse over sidebar pokemon
			// pokemon definitely exists, serverPokemon always ignored
			let sideIndex = parseInt(args[1], 10);
			let side = this.battle.sides[sideIndex];
			let pokemon = side.pokemon[parseInt(args[2], 10)];
			if (args[3] === 'illusion') {
				buf = '';
				const species = pokemon.getBaseSpecies().baseSpecies;
				let index = 1;
				for (const otherPokemon of side.pokemon) {
					if (otherPokemon.getBaseSpecies().baseSpecies === species) {
						buf += this.showPokemonTooltip(otherPokemon, null, false, index);
						index++;
					}
				}
			} else {
				buf = this.showPokemonTooltip(pokemon);
			}
			break;
		}
		case 'activepokemon': { // activepokemon|SIDE|ACTIVE
			// mouse over active pokemon
			// pokemon definitely exists, serverPokemon maybe
			let sideIndex = parseInt(args[1], 10);
			let side = this.battle.sides[+this.battle.viewpointSwitched ^ sideIndex];
			let activeIndex = parseInt(args[2], 10);
			let pokemonIndex = activeIndex;
			if (activeIndex >= 1 && this.battle.sides.length > 2) {
				pokemonIndex -= 1;
				side = this.battle.sides[side.n + 2];
			}
			let pokemon = side.active[activeIndex];
			let serverPokemon = null;
			if (side === this.battle.mySide && this.battle.myPokemon) {
				serverPokemon = this.battle.myPokemon[pokemonIndex];
			}
			if (side === this.battle.mySide.ally && this.battle.myAllyPokemon) {
				serverPokemon = this.battle.myAllyPokemon[pokemonIndex];
			}
			if (!pokemon) return false;
			buf = this.showPokemonTooltip(pokemon, serverPokemon, true);
			break;
		}
		case 'switchpokemon': { // switchpokemon|POKEMON
			// mouse over switchable pokemon
			// serverPokemon definitely exists, sidePokemon maybe
			// let side = this.battle.mySide;
			let activeIndex = parseInt(args[1], 10);
			let pokemon = null;
			/* if (activeIndex < side.active.length && activeIndex < this.battle.pokemonControlled) {
				pokemon = side.active[activeIndex];
				if (pokemon && pokemon.side === side.ally) pokemon = null;
			} */
			let serverPokemon = this.battle.myPokemon![activeIndex];
			if (serverPokemon.active && toID(serverPokemon.ability || serverPokemon.baseAbility) === 'perfectforecast') {
				pokemon = this.battle.mySide.active[activeIndex] || null;
			}
			buf = this.showPokemonTooltip(pokemon, serverPokemon, !!pokemon);
			break;
		}
		case 'allypokemon': { // allypokemon|POKEMON
			// mouse over ally's pokemon in multi battles
			// serverPokemon definitely exists, sidePokemon maybe
			// let side = this.battle.mySide.ally;
			let activeIndex = parseInt(args[1], 10);
			let pokemon = null;
			/*if (activeIndex < side.pokemon.length) {
				pokemon = side.pokemon[activeIndex] || side.ally ? side.ally.pokemon[activeIndex] : null;
			}*/
			let serverPokemon = this.battle.myAllyPokemon ? this.battle.myAllyPokemon[activeIndex] : null;
			buf = this.showPokemonTooltip(pokemon, serverPokemon);
			break;
		}
		case 'field': {
			buf = this.showFieldTooltip();
			break;
		}
		default:
			// "throws" an error without crashing
			Promise.resolve(new Error(`unrecognized type`));
			buf = `<p class="message-error" style="white-space: pre-wrap">${new Error(`unrecognized type`).stack}</p>`;
		}

		this.placeTooltip(buf, elem, ownHeight, type);
		return true;
	}

	placeTooltip(innerHTML: string, hoveredElem?: HTMLElement, notRelativeToParent?: boolean, type?: string) {
		let $elem;
		if (hoveredElem) {
			$elem = $(hoveredElem);
		} else {
			$elem = (this.battle.scene as BattleScene).$turn;
			notRelativeToParent = true;
		}

		let hoveredX1 = $elem.offset()!.left;

		if (!notRelativeToParent) {
			$elem = $elem.parent();
		}

		let hoveredY1 = $elem.offset()!.top;
		let hoveredY2 = hoveredY1 + $elem.outerHeight()!;

		// (x, y) are the left and top offsets of #tooltipwrapper, which mark the
		// BOTTOM LEFT CORNER of the tooltip

		let x = Math.max(hoveredX1 - 2, 0);
		let y = Math.max(hoveredY1 - 5, 0);

		let $wrapper = $('#tooltipwrapper');
		if (!$wrapper.length) {
			$wrapper = $(`<div id="tooltipwrapper" role="tooltip"></div>`);
			$(document.body).append($wrapper);
			$wrapper.on('click', e => {
				try {
					const selection = window.getSelection()!;
					if (selection.type === 'Range') return;
				} catch (err) {}
				BattleTooltips.hideTooltip();
			});
		} else {
			$wrapper.removeClass('tooltip-locked');
		}
		$wrapper.css({
			left: x,
			top: y,
		});
		innerHTML = `<div class="tooltipinner"><div class="tooltip tooltip-${type}">${innerHTML}</div></div>`;
		$wrapper.html(innerHTML).appendTo(document.body);
		BattleTooltips.elem = $wrapper.find('.tooltip')[0] as HTMLDivElement;
		BattleTooltips.isLocked = false;

		let height = $(BattleTooltips.elem).outerHeight()!;
		if (y - height < 1) {
			// tooltip is too tall to fit above the element:
			// try to fit it below it instead
			y = hoveredY2 + height + 5;
			if (y > document.documentElement.clientHeight) {
				// tooltip is also too tall to fit below the element:
				// just place it at the top of the screen
				y = height + 1;
			}
			$wrapper.css('top', y);
		} else if (y < 75) {
			// tooltip is pretty high up, put it below the element if it fits
			y = hoveredY2 + height + 5;
			if (y < document.documentElement.clientHeight) {
				// it fits
				$wrapper.css('top', y);
			}
		}

		let width = $(BattleTooltips.elem).outerWidth()!;
		if (x > document.documentElement.clientWidth - width - 2) {
			x = document.documentElement.clientWidth - width - 2;
			$wrapper.css('left', x);
		}

		BattleTooltips.parentElem = hoveredElem || null;
		return true;
	}

	hideTooltip() {
		BattleTooltips.hideTooltip();
	}

	static zMoveEffects: {[zEffect: string]: string} = {
		'clearnegativeboost': "Restores negative stat stages to 0",
		'crit2': "Crit ratio +2",
		'heal': "Restores HP 100%",
		'curse': "Restores HP 100% if user is Ghost type, otherwise Attack +1",
		'redirect': "Redirects opposing attacks to user",
		'healreplacement': "Restores replacement's HP 100%",
	};

	getStatusZMoveEffect(move: Move) {
		if (move.zMove!.effect! in BattleTooltips.zMoveEffects) {
			return BattleTooltips.zMoveEffects[move.zMove!.effect!];
		}
		let boostText = '';
		if (move.zMove!.boost) {
			let boosts = Object.keys(move.zMove!.boost) as StatName[];
			boostText = boosts.map(stat =>
				BattleTextParser.stat(stat) + ' +' + move.zMove!.boost![stat]
			).join(', ');
		}
		return boostText;
	}

	static zMoveTable: {[type in TypeName]: string} = {
		Poison: "Acid Downpour",
		Fighting: "All-Out Pummeling",
		Dark: "Black Hole Eclipse",
		Grass: "Bloom Doom",
		Normal: "Breakneck Blitz",
		Rock: "Continental Crush",
		Steel: "Corkscrew Crash",
		Dragon: "Devastating Drake",
		Electric: "Gigavolt Havoc",
		Water: "Hydro Vortex",
		Fire: "Inferno Overdrive",
		Ghost: "Never-Ending Nightmare",
		Bug: "Savage Spin-Out",
		Psychic: "Shattered Psyche",
		Ice: "Subzero Slammer",
		Flying: "Supersonic Skystrike",
		Ground: "Tectonic Rage",
		Fairy: "Twinkle Tackle",
		Stellar: "",
		"???": "",
	};
	static getZMoveBasePower(move: Move) {
		let basePower = move.basePower;
		if (typeof move.multihit === 'number') {
			basePower *= move.multihit;
		} else {
			if (move.zMove?.basePower) return move.zMove.basePower;
			if (Array.isArray(move.multihit)) basePower *= 3;
		}
		if (!basePower) return 100;
		if (basePower >= 140) return 200;
		if (basePower >= 130) return 195;
		if (basePower >= 120) return 190;
		if (basePower >= 110) return 185;
		if (basePower >= 100) return 180;
		if (basePower >= 90) return 175;
		if (basePower >= 80) return 160;
		if (basePower >= 70) return 140;
		if (basePower >= 60) return 120;
		return 100;
	}

	static maxMoveTable: {[type in TypeName]: string} = {
		Poison: "Max Ooze",
		Fighting: "Max Knuckle",
		Dark: "Max Darkness",
		Grass: "Max Overgrowth",
		Normal: "Max Strike",
		Rock: "Max Rockfall",
		Steel: "Max Steelspike",
		Dragon: "Max Wyrmwind",
		Electric: "Max Lightning",
		Water: "Max Geyser",
		Fire: "Max Flare",
		Ghost: "Max Phantasm",
		Bug: "Max Flutterby",
		Psychic: "Max Mindstorm",
		Ice: "Max Hailstorm",
		Flying: "Max Airstream",
		Ground: "Max Quake",
		Fairy: "Max Starfall",
		Stellar: "",
		"???": "",
	};

	getMaxMoveFromType(type: TypeName, gmaxMove?: string | Move) {
		if (gmaxMove) {
			gmaxMove = Dex.moves.get(gmaxMove);
			if (type === gmaxMove.type) return gmaxMove;
		}
		return Dex.moves.get(BattleTooltips.maxMoveTable[type]);
	}

	static getNaturePowerMove(battle: Battle): Move {
		if (battle.gen <= 5) return battle.dex.moves.get(battle.gen <= 3 ? 'swift' : 'earthquake');
		const data = window as any;
		if (battle.hasPseudoWeather('Frozen Aura')) {
			return battle.dex.moves.get(data.BattleAuraRules?.frozenaura?.naturePowerMove || 'icebeam');
		}
		if (battle.hasPseudoWeather('Ashen Aura')) {
			return battle.dex.moves.get(data.BattleAuraRules?.ashenaura?.naturePowerMove || 'infernalparade');
		}
		if (battle.hasPseudoWeather('Rift Aura')) {
			return battle.dex.moves.get(data.BattleAuraRules?.riftaura?.naturePowerMove || 'darkpulse');
		}
		const field = data.BattleFieldRules?.find((entry: any) => battle.hasPseudoWeather(entry.name));
		const mapping = data.BattleNaturePowerMoves || {};
		const fieldId = field?.id || battle.pseudoWeather.map(([name]) => toID(name)).find(id => mapping[id]) || '';
		return battle.dex.moves.get(mapping[fieldId] || 'triattack');
	}

	showMoveTooltip(move: Move, isZOrMax: string, pokemon: Pokemon, serverPokemon: ServerPokemon, gmaxMove?: Move) {
		if (move.id === 'wavecrash' && !isZOrMax && new ModifiableValue(this.battle, pokemon, serverPokemon).tryAbility('Monarch of Ice')) move = this.battle.dex.moves.get('royalcollision');
		const naturePowerPreview = move.id === 'naturepower' && isZOrMax !== 'zmove' && isZOrMax !== 'maxmove';
		if (naturePowerPreview) move = BattleTooltips.getNaturePowerMove(this.battle);
		const gardenStage = [1, 2, 3, 4, 5].find(stage => this.battle.hasPseudoWeather(`Flower Garden ${stage}`)) || 0;
		const gardenSecretPower = move.id === 'secretpower' && isZOrMax !== 'zmove' && isZOrMax !== 'maxmove' && [1, 5].includes(gardenStage);
		if (gardenSecretPower) move = this.battle.dex.moves.get(gardenStage === 5 ? 'Petal Dance' : 'Sweet Scent');
		let text = '';

		let zEffect = '';
		const baseMove = move;
		let zMoveBasePower = 0;
		let foeActive = pokemon.side.foe.active;
		if (this.battle.gameType === 'freeforall') {
			foeActive = [...foeActive, ...pokemon.side.active].filter(active => active !== pokemon);
		}
		// TODO: move this somewhere it makes more sense
		if (pokemon.ability === '(suppressed)') serverPokemon.ability = '(suppressed)';
		let ability = toID(serverPokemon.ability || pokemon.ability || serverPokemon.baseAbility);
		let item = this.battle.dex.items.get(serverPokemon.item);

		let value = new ModifiableValue(this.battle, pokemon, serverPokemon);
		let [moveType, category] = this.getMoveType(move, value, isZOrMax === 'maxmove' ? gmaxMove || true : undefined);
		let categoryDiff = move.category !== category;
		move = new Move(move.id, '', { ...move, category });

		if (isZOrMax === 'zmove') {
			const zMoveFrom = item.zMoveFrom && (Array.isArray(item.zMoveFrom) ? item.zMoveFrom : [item.zMoveFrom]);
			if (zMoveFrom?.includes(move.name)) {
				move = gmaxMove?.isZ ? gmaxMove : this.battle.dex.moves.get(item.zMove as string);
				zMoveBasePower = move.basePower;
			} else if (move.category === 'Status') {
				move = new Move(move.id, "", {
					...move,
					name: 'Z-' + move.name,
				});
				zEffect = this.getStatusZMoveEffect(move);
			} else {
				let moveName = gmaxMove?.isZ ? gmaxMove.name : BattleTooltips.zMoveTable[item.zMoveType as TypeName];
				let zMove = gmaxMove?.isZ ? gmaxMove : this.battle.dex.moves.get(moveName);
				let movePower = BattleTooltips.getZMoveBasePower(move);
				// the different Hidden Power types don't have a Z power set, fall back on base move
				if (!movePower && move.id.startsWith('hiddenpower')) {
					movePower = this.battle.dex.moves.get('hiddenpower').zMove?.basePower || 0;
				}
				if (move.id === 'weatherball' && !gmaxMove?.isZ) {
					switch (this.battle.weather) {
					case 'sunnyday':
					case 'desolateland':
						zMove = this.battle.dex.moves.get(BattleTooltips.zMoveTable['Fire']);
						break;
					case 'raindance':
					case 'primordialsea':
						zMove = this.battle.dex.moves.get(BattleTooltips.zMoveTable['Water']);
						break;
					case 'sandstorm':
						zMove = this.battle.dex.moves.get(BattleTooltips.zMoveTable['Rock']);
						break;
					case 'hail':
					case 'snow':
						zMove = this.battle.dex.moves.get(BattleTooltips.zMoveTable['Ice']);
						break;
					}
				}
				move = new Move(zMove.id, zMove.name, {
					...zMove,
					// Server Z conversion uses the base data category before ordinary adaptive callbacks.
					category: baseMove.category,
					basePower: movePower,
				});
				zMoveBasePower = move.basePower;
				categoryDiff = false;
			}
			[moveType, category] = this.getMoveType(move, value);
			categoryDiff = false;
		} else if (isZOrMax === 'maxmove') {
			if (move.category === 'Status') {
				move = this.battle.dex.moves.get('Max Guard');
			} else {
				let maxMove = this.getMaxMoveFromType(moveType, gmaxMove);
				const basePower = ['gmaxdrumsolo', 'gmaxfireball', 'gmaxhydrosnipe'].includes(maxMove.id) ?
					maxMove.basePower : move.maxMove.basePower;
				move = new Move(maxMove.id, maxMove.name, {
					...maxMove,
					category: move.category,
					basePower,
				});
				categoryDiff = false;
			}
		}

		if (categoryDiff) {
			move = new Move(move.id, move.name, {
				...move,
				category,
			});
		}

		text += '<h2>' + (naturePowerPreview ? 'Nature Power &rarr; ' : '') + (move.id === 'vorpalblade' && this.battle.hasPseudoWeather('Glitch Terrain') ? 'Unown Buster (Vorpal Blade)' : move.name) + '<br />';
		const crescentRend = toID(pokemon.effectiveAbility(serverPokemon || undefined)) === 'crescentrend' &&
			category !== 'Status' && moveType === 'Flying';
		const rupturedHeaven = toID(pokemon.effectiveAbility(serverPokemon || undefined)) === 'rupturedheaven' && !!move.flags.slicing;
		if (crescentRend || rupturedHeaven) {
			const flags = {...move.flags};
			delete flags.contact;
			move = new Move(move.id, move.name, {...move, flags});
		}

		text += Dex.getTypeIcon(moveType);
		text += ['superumdmove', 'beasthunter', 'attackorder'].includes(move.id) ? ' Physical / Special</h2>' : ` ${Dex.getCategoryIcon(category)}</h2>`;
		if (naturePowerPreview) text += '<p>Calls this move in the current environment. Uses Nature Power\'s PP and status-move priority. Details below describe the called move.</p>';
		if (rupturedHeaven) text += '<p>Ruptured Heaven: slicing moves are non-contact and receive Sharpness.</p>';
		if (toID(pokemon.effectiveAbility(serverPokemon || undefined)) === 'burningquake') text += '<p>Burning Quake: +1 critical-hit ratio; after a move deals critical opposing HP damage, +1 Speed once per turn.</p>';
		if (['superumdmove', 'beasthunter', 'attackorder'].includes(move.id)) text += '<p>Chooses the category dealing greater calculated damage to the actual target; depends on both Pokémon and battle conditions.</p>';
		if (move.id === 'thunderraid') text += '<p>Hit power: 20 / 40 / 60. Each hit checks accuracy separately; stops on a miss. Base power below is the first hit.</p>';
		if (crescentRend) {
			text += '<p>Crescent Rend: non-contact; ignores Reflect, Light Screen and Aurora Veil. ' +
				'Excess damage from breaking a Substitute carries into its owner as part of this hit.</p>';
		}
		if (toID(pokemon.effectiveAbility(serverPokemon || undefined)) === 'exaltedaether' &&
			category === 'Physical' && ['Dragon', 'Flying'].includes(moveType) &&
			!move.multihit && !move.flags.noparentalbond && !move.flags.charge && !move.flags.futuremove &&
			!move.ohko && !move.isZ && !move.isMax &&
			['normal', 'adjacentFoe', 'any', 'randomNormal'].includes(move.target)) {
			text += '<p>Exalted Aether: two strikes. First: normal damage, heals 25% of opposing HP damage. ' +
				'Second: 40% damage, ignores positive Defense stages. Defensive screens apply normally to both strikes. ' +
				'Secondary and user effects occur once. Power below describes the first strike.</p>';
		}
		if (toID(pokemon.effectiveAbility(serverPokemon)) === 'auraconvergence' &&
			category !== 'Status' && ['Fighting', 'Steel'].includes(moveType) && !move.damage) {
			text += '<p>Aura Convergence: physical or special is chosen separately for each target from staged stats. ' +
				'The icon shows the original category; burn and screens apply after the choice.</p>';
		}
		if (isZOrMax === 'zmove') {
			text += '<p>Powered-up Z-Move from ' + baseMove.name + '.</p>';
			if (zMoveBasePower) text += '<p>Z-Move base power: ' + zMoveBasePower + '</p>';
		}
		const fieldNotes = (window as any).BattleFieldTooltips?.activeNotes(this.battle, move, pokemon, serverPokemon, foeActive[0]);
		if (fieldNotes) text += '<p class="tooltip-section">' + BattleLog.escapeHTML(fieldNotes) + '</p>';
		if (gardenSecretPower) text += `<p>Secret Power becomes ${move.name} in Flower Garden Stage ${gardenStage}.</p>`;
		if (this.battle.hasPseudoWeather('Flower Garden 1') && ['growth', 'rototiller'].includes(move.id)) {
			text += '<p>Flower Garden: +2 Attack and +2 Sp. Atk.' +
				(move.id === 'rototiller' ? ' Also affects the grounded user regardless of type.' : '') + '</p>';
		}

		// Check if there are more than one active Pokémon to check for multiple possible BPs.
		let showingMultipleBasePowers = move.id === 'tripledive';
		if (move.id === 'tripledive') {
			text += '<p>Base power per hit: 20 / 40 / 60 before modifiers. Each hit checks accuracy.</p>' +
				'<p>Successful field transitions apply before that hit: Water Surface, then Underwater. Starting on Icy: Icy, Water Surface, Underwater. Field locks apply.</p>' +
				'<p>On either water field: 1.5x named boost and the existing Water boost (normally 2.25x total).</p>';
		}
		if (category !== 'Status' && foeActive.length > 1 && move.id !== 'tripledive') {
			// We check if there is a difference in base powers to note it.
			// Otherwise, it is just shown as in singles.
			// The trick is that we need to calculate it first for each Pokémon to see if it changes.
			let prevBasePower: string | null = null;
			let basePower: string = '';
			let difference = false;
			let basePowers = [];
			for (const active of foeActive) {
				if (!active) continue;
				value = this.getMoveBasePower(move, moveType, value, active);
				basePower = '' + value;
				if (prevBasePower === null) prevBasePower = basePower;
				if (prevBasePower !== basePower) difference = true;
				basePowers.push((isZOrMax === 'zmove' ? 'Current power' : 'Base power') +
					' vs ' + active.name + ': ' + basePower);
			}
			if (difference) {
				text += '<p>' + basePowers.join('<br />') + '</p>';
				showingMultipleBasePowers = true;
			}
			// Falls through to not to repeat code on showing the base power.
		}
		if (!showingMultipleBasePowers && category !== 'Status') {
			let activeTarget = foeActive[0] || foeActive[1] || foeActive[2];
			value = this.getMoveBasePower(move, moveType, value, activeTarget);
			text += '<p>' + (isZOrMax === 'zmove' ? 'Current power' : 'Base power') + ': ' + value + '</p>';
		}


		const gilded = Dex.getGildedMoveProfile(move, pokemon.getTypes(serverPokemon)[0], pokemon.effectiveAbility(serverPokemon), this.battle.gameType) || Dex.getSilkenMoveProfile(move, pokemon.effectiveAbility(serverPokemon), serverPokemon.item);
		if (gilded && !isZOrMax) text += '<p>' + BattleLog.escapeHTML(gilded.text) + ' before conditional modifiers.</p>';
		let accuracy = this.getMoveAccuracy(move, value);

		// Deal with Nature Power special case, indicating which move it calls.
		if (move.id === 'naturepower') {
			const calledMove = BattleTooltips.getNaturePowerMove(this.battle);
			text += '<p>Calls ' + Dex.getTypeIcon(this.getMoveType(calledMove, value)[0]) + ' ' + calledMove.name + '</p>';
		}

		text += '<p>Accuracy: ' + accuracy + '</p>';
		if (!isZOrMax && move.id === 'wildcharge' && pokemon.volatiles.stormcharge && value.tryAbility('Storm Memory')) text += '<p>Storm Charge: this Wild Charge uses the stronger offensive stat, gains 50% power and causes no recoil. The charge is spent only on damage to a foe or Substitute.</p>';
		if (category !== 'Status' && value.tryAbility('Monarch of Ice')) text += '<p>Monarch of Ice: 30% frostbite chance. Critically hits frostbitten foes, then thaws them after the attack. Ice moves receive STAB.</p>';
		if (category !== 'Status' && moveType === 'Flying' && value.tryAbility("Emperor's Resolve")) text += '<p>Emperor’s Resolve: this move receives Flying STAB.</p>';
		if (category !== 'Status' && moveType === 'Psychic' && value.tryAbility("Oracle's Refrain")) {
			text += '<p>Oracle’s Refrain: this move receives Psychic STAB.</p>';
		}
		if ((move.flags as any).kick && value.tryAbility('Springheel')) {
			text += '<p>Springheel: ignores positive defensive stages; no crash damage.</p>';
		}
		if (category !== 'Status' && ['Fire', 'Electric'].includes(moveType) && value.tryAbility('Plasma Eruption')) {
			text += '<p>Plasma Eruption: may switch Fire/Electric typing. Power shown is for the displayed type; conversion can change field and phase bonuses.</p>';
			text += '<p>Electric burns; Fire paralyzes. Pressure rises once per successful attack.</p>';
		}
		if (category !== 'Status' && this.battle.hasPseudoWeather('Wonder Room')) {
			text += '<p>Wonder Room prevents critical hits.</p>';
		}
		if (this.battle.hasPseudoWeather('Gravity')) {
			text += '<p>Gravity ignores positive evasion stages on affected targets.</p>';
			if (['dig', 'dive'].includes(move.id)) {
				text += '<p>Gravity: attacks immediately without consuming Power Herb.</p>';
			}
		}
		if (this.battle.hasPseudoWeather('Magic Room') &&
			['reflect', 'lightscreen', 'auroraveil', 'arenitewall', 'atlantiswall', 'safeguard', 'mist'].includes(move.id)) {
			text += '<p>Magic Room: lasts 8 turns if successfully created.</p>';
		}
		if (this.battle.hasPseudoWeather('Magic Room')) {
			if (move.id === 'magiccoat') text += '<p>Magic Room: also shields current active allies for this turn.</p>';
			if (move.id === 'healpulse') text += '<p>Magic Room: heals 75% max HP; does not stack with Mega Launcher.</p>';
		}
		if (this.battle.hasPseudoWeather('Wonder Room')) {
			const roomNotes: {[id: string]: string} = {
				guardsplit: 'after averaging defenses, raises both participants\' Defense and Sp. Def by 1.',
				powersplit: 'after averaging offenses, lowers both participants\' Attack and Sp. Atk by 1.',
				guardswap: 'after a successful swap, clears the user\'s negative Defense and Sp. Def stages.',
				powerswap: 'after a successful swap, clears the user\'s negative Attack and Sp. Atk stages.',
				heartswap: 'after a successful swap, clears the user\'s negative stat stages.',
				speedswap: 'also swaps Speed stages, then clears the user\'s negative Speed.',
				painsplit: 'also clears confusion on both participants after a successful split.',
				powertrick: 'also exchanges Attack and Defense stages when toggled on or off.',
				powershift: 'activation also swaps Sp. Atk and Sp. Def until toggled off or switched out; Baton Pass transfers it.',
			};
			if (roomNotes[move.id]) text += '<p>Wonder Room: ' + roomNotes[move.id] + '</p>';
		}
		if (zEffect) text += '<p>Z-Effect: ' + zEffect + '</p>';
		if (category !== 'Status' && (pokemon.terastallized === 'Stellar' || isZOrMax === 'zmove' || move.isZ)) {
			text += '<p>Against eligible ???-type targets: 1.5&times; calculated damage. Stellar and Z-Move qualification does not stack; fixed damage is unchanged.</p>';
		}

		if (this.battle.hardcoreMode) {
			text += '<p class="tooltip-section">' + move.shortDesc + '</p>';
		} else {
			text += '<p class="tooltip-section">';
			if (move.priority > 1) {
				text += 'Nearly always moves first <em>(priority +' + move.priority + ')</em>.</p><p>';
			} else if (move.priority <= -1) {
				text += 'Nearly always moves last <em>(priority &minus;' + (-move.priority) + ')</em>.</p><p>';
			} else if (move.priority === 1) {
				text += 'Usually moves first <em>(priority +' + move.priority + ')</em>.</p><p>';
			} else {
				if (move.id === 'grassyglide' && this.battle.hasPseudoWeather('Grassy Terrain')) {
					text += 'Usually moves first <em>(priority +1)</em>.</p><p>';
				}
			}

			text += '' + (move.desc || move.shortDesc || '') + '</p>';

			if (this.battle.gameType === 'doubles' || this.battle.gameType === 'multi') {
				if (move.target === 'allAdjacent') {
					text += '<p>&#x25ce; Hits both foes and ally.</p>';
				} else if (move.target === 'allAdjacentFoes') {
					text += '<p>&#x25ce; Hits both foes.</p>';
				}
			} else if (this.battle.gameType === 'triples') {
				if (move.target === 'allAdjacent') {
					text += '<p>&#x25ce; Hits adjacent foes and allies.</p>';
				} else if (move.target === 'allAdjacentFoes') {
					text += '<p>&#x25ce; Hits adjacent foes.</p>';
				} else if (move.target === 'any') {
					text += '<p>&#x25ce; Can target distant Pok&eacute;mon in Triples.</p>';
				}
			} else if (this.battle.gameType === 'freeforall') {
				if (move.target === 'allAdjacent' || move.target === 'allAdjacentFoes') {
					text += '<p>&#x25ce; Hits all foes.</p>';
				} else if (move.target === 'adjacentAlly') {
					text += '<p>&#x25ce; Can target any foe in Free-For-All.</p>';
				}
			}

			if (move.flags.defrost) {
				text += `<p class="movetag">The user thaws out if it is frozen.</p>`;
			}
			if (!move.flags.protect && !['self', 'allySide'].includes(move.target)) {
				text += `<p class="movetag">Not blocked by Protect <small>(and Detect, King's Shield, Spiky Shield)</small></p>`;
			}
			if (move.flags.bypasssub) {
				text += `<p class="movetag">Bypasses Substitute <small>(but does not break it)</small></p>`;
			}
			if (!move.flags.reflectable && !['self', 'allySide'].includes(move.target) && move.category === 'Status') {
				text += `<p class="movetag">&#x2713; Not bounceable <small>(can't be bounced by Magic Coat/Bounce)</small></p>`;
			}

			if (move.flags.contact) {
				text += `<p class="movetag">&#x2713; Contact <small>(triggers Iron Barbs, Spiky Shield, etc)</small></p>`;
			}
			if (move.flags.sound) {
				text += `<p class="movetag">&#x2713; Sound <small>(doesn't affect Soundproof pokemon)</small></p>`;
			}
			if (move.flags.powder && this.battle.gen > 5) {
				text += `<p class="movetag">&#x2713; Powder <small>(doesn't affect Grass, Overcoat, Safety Goggles)</small></p>`;
			}
			if (move.flags.punch && ability === 'ironfist') {
				text += `<p class="movetag">&#x2713; Fist <small>(boosted by Iron Fist)</small></p>`;
			}
			if (move.flags.pulse && ability === 'megalauncher') {
				text += `<p class="movetag">&#x2713; Pulse <small>(boosted by Mega Launcher)</small></p>`;
			}
			if (move.flags.bite && ability === 'strongjaw') {
				text += `<p class="movetag">&#x2713; Bite <small>(boosted by Strong Jaw)</small></p>`;
			}
			if ((move.recoil || move.hasCrashDamage) && ability === 'reckless') {
				text += `<p class="movetag">&#x2713; Recoil <small>(boosted by Reckless)</small></p>`;
			}
			if (move.flags.bullet) {
				text += `<p class="movetag">&#x2713; Bullet-like <small>(doesn't affect Bulletproof pokemon)</small></p>`;
			}
			if (move.flags.arrow) {
				text += `<p class="movetag">&#x2713; Arrow <small>(Fallen Star / Dual Wield)</small></p>`;
			}
			if (move.flags.horn) {
				text += `<p class="movetag">&#x2713; Horn <small>(Power Drill / Dual Wield)</small></p>`;
			}
			if (move.flags.drill) {
				text += `<p class="movetag">&#x2713; Drill <small>(boosted by Power Drill / Dual Wield)</small></p>`;
			}
			if (move.flags.slicing) {
				text += `<p class="movetag">&#x2713; Slicing <small>(boosted by Sharpness)</small></p>`;
			}
			if (move.flags.wind) {
				text += `<p class="movetag">&#x2713; Wind <small>(activates Wind Power and Wind Rider)</small></p>`;
			}
		}
		return text;
	}

	/**
	 * Needs either a Pokemon or a ServerPokemon, but note that neither
	 * are guaranteed: If you hover over a possible switch-in that's
	 * never been switched in before, you'll only have a ServerPokemon,
	 * and if you hover over an opponent's pokemon, you'll only have a
	 * Pokemon.
	 *
	 * isActive is true if hovering over a pokemon in the battlefield,
	 * and false if hovering over a pokemon in the Switch menu.
	 *
	 * @param clientPokemon
	 * @param serverPokemon
	 * @param isActive
	 */
	renderAdaptation(memory: NonNullable<Pokemon['adaptation']>) {
		const esc = (value: any) => BattleLog.escapeHTML(String(value));
		const progress = (value: number, total: number) => value >= total ? 'Ready' : value + '/' + total;
		const names: {[id: string]: string} = {
			brn: 'Burn', psn: 'Poison', tox: 'Poison', par: 'Paralysis', slp: 'Sleep', frz: 'Freeze',
			sunnyday: 'Sun', desolateland: 'Harsh sun', raindance: 'Rain', primordialsea: 'Heavy rain',
			sandstorm: 'Sandstorm', hail: 'Hail', flowergarden: 'Flower Garden',
			murkwatersurfaceterrain: 'Murkwater Surface', watersurfaceterrain: 'Water Surface',
			underwaterterrain: 'Underwater', factoryterrain: 'Factory',
		};
		const name = (id: string) => names[id] ||
			(window as any).BattleFieldRules?.find((field: any) => field.id === id)?.name ||
			(Dex.moves.get(id).exists ? Dex.moves.get(id).name : id.replace(/terrain$/, '').replace(/^./, c => c.toUpperCase()));
		let text = '<div class="tooltip-section"><strong>Adaptive Cycle' + (memory.active ? '' : ' (paused)') + '</strong><br />';
		text += '<b>Z-Move weakness:</b> 4× damage through Protect, weather and type immunity.<br />';
		[true, false].forEach(active => {
			const types = Object.entries(memory.types || {}).filter(([type]) => memory.activeTypes.includes(type) === active);
			if (types.length) text += '<b>' + (active ? 'Taking less damage' : 'Stored for later') + ':</b> ' +
				types.map(([type, record]) => esc(type) + ' ' + [0,20,35,50][record.stage] + '%').join(', ') + '<br />';
		});
		const foes = Object.values(memory.opponents || {});
		const label = (record: typeof foes[number]) => esc(record.label.replace(/^p[1-4][a-z]?:\s*/, ''));
		const adapted = foes.filter(foe => foe.complete);
		if (adapted.length) {
			text += '<b>Fully adapted:</b> ' + adapted.map(label).join(', ') + '<br />';
			text += '<small>Resists their attacks. Its own attacks hit them super effectively and bypass their immunities.</small><br />';
		}
		for (const foe of foes.filter(record => !record.complete)) {
			const notes = [];
			if (foe.moves.length) notes.push(foe.moves.map(id => esc(Dex.moves.get(id).name)).join(', '));
			if (foe.setup.stage) notes.push(foe.setup.stage >= 2 ? 'counters stat boosts' : 'learning stat boosts');
			if (foe.bypass.length) notes.push('recognizes ability bypass');
			if (foe.defenses.length) notes.push('recognizes defensive effects');
			text += '<b>Learning ' + label(foe) + ':</b> ' + foe.points + '/4' +
				(notes.length ? ' <small>(' + notes.join(' · ') + ')</small>' : '') + '<br />';
		}
		([
			['Status', memory.statuses, 2], ['Chip damage', memory.chip, 2],
			['Fields', memory.fields, 3], ['Weather', memory.weather, 3],
		] as [string, {[id: string]: {stage: number}}, number][]).forEach(([label, table, total]) => {
			const rows = Object.entries(table || {});
			if (!rows.length) return;
			text += '<b>' + label + ':</b> ' + rows.map(([id, record]) =>
				esc(name(id)) + (id === memory.field || id === memory.currentWeather ? ' (active)' : '') + ' · ' +
				(record.stage >= total ? 'adapted' : progress(record.stage, total))
			).join(', ') + '<br />';
		});
		return text + '</div>';
	}

	showPokemonTooltip(
		clientPokemon: Pokemon | null, serverPokemon?: ServerPokemon | null, isActive?: boolean, illusionIndex?: number
	) {
		const pokemon = clientPokemon || serverPokemon!;
		let text = clientPokemon?.volatiles.sealing ? '<p><strong>Sealing:</strong> Shared moves are sealed while Keystone Seal is active.</p>' : '';
		let genderBuf = '';
		const gender = pokemon.gender;
		if (gender === 'M' || gender === 'F') {
			genderBuf = ` <img src="${Dex.fxPrefix}gender-${gender.toLowerCase()}.png" alt="${gender}" width="7" height="10" class="pixelated" /> `;
		}

		let name = BattleLog.escapeHTML(pokemon.name);
		if (pokemon.speciesForme !== pokemon.name) {
			name += ' <small>(' + BattleLog.escapeHTML(pokemon.speciesForme) + ')</small>';
		}

		let levelBuf = (pokemon.level !== 100 ? ` <small>L${pokemon.level}</small>` : ``);
		if (!illusionIndex || illusionIndex === 1) {
			text += `<h2>${name}${genderBuf}${illusionIndex ? '' : levelBuf}<br />`;

			if (clientPokemon?.volatiles.formechange) {
				if (clientPokemon.volatiles.transform) {
					text += `<small>(Transformed into ${clientPokemon.volatiles.formechange[1]})</small><br />`;
				} else {
					text += `<small>(Changed forme: ${clientPokemon.volatiles.formechange[1]})</small><br />`;
				}
			}

			let types = serverPokemon?.terastallized ? [serverPokemon.teraType] : this.getPokemonTypes(pokemon);
			let knownPokemon = serverPokemon || clientPokemon!;

			if (pokemon.terastallized) {
				text += `<small>(Terastallized)</small><br />`;
			} else if (clientPokemon?.volatiles.typechange || clientPokemon?.volatiles.typeadd) {
				text += `<small>(Type changed)</small><br />`;
			}
			text += `<span class="textaligned-typeicons">${types.map(type => Dex.getTypeIcon(type)).join(' ')}</span>`;
			if (pokemon.terastallized) {
				text += `&nbsp; &nbsp; <small>(base: <span class="textaligned-typeicons">${this.getPokemonTypes(pokemon, true).map(type => Dex.getTypeIcon(type)).join(' ')}</span>)</small>`;
			} else if (knownPokemon.teraType && !this.battle.rules['Terastal Clause']) {
				text += `&nbsp; &nbsp; <small>(Tera Type: <span class="textaligned-typeicons">${Dex.getTypeIcon(knownPokemon.teraType)}</span>)</small>`;
			}
			text += `</h2>`;
		}

		if (illusionIndex) {
			text += `<p class="tooltip-section"><strong>Possible Illusion #${illusionIndex}</strong>${levelBuf}</p>`;
		}

		if (pokemon.fainted) {
			text += '<p><small>HP:</small> (fainted)</p>';
		} else if (this.battle.hardcoreMode) {
			if (serverPokemon) {
				text += '<p><small>HP:</small> ' + serverPokemon.hp + '/' + serverPokemon.maxhp + (pokemon.status ? ' <span class="status ' + pokemon.status + '">' + pokemon.status.toUpperCase() + '</span>' : '') + '</p>';
			}
		} else {
			let exacthp = '';
			if (serverPokemon) {
				exacthp = ' (' + serverPokemon.hp + '/' + serverPokemon.maxhp + ')';
			} else if (pokemon.maxhp === 48) {
				exacthp = ' <small>(' + pokemon.hp + '/' + pokemon.maxhp + ' pixels)</small>';
			}
			text += '<p><small>HP:</small> ' + Pokemon.getHPText(pokemon) + exacthp + (pokemon.status ? ' <span class="status ' + pokemon.status + '">' + pokemon.status.toUpperCase() + '</span>' : '');
			if (clientPokemon) {
				if (pokemon.status === 'tox') {
					if (pokemon.ability === 'Poison Heal' || pokemon.ability === 'Magic Guard') {
						text += ' <small>Would take if ability removed: ' + Math.floor(100 / 16 * Math.min(clientPokemon.statusData.toxicTurns + 1, 15)) + '%</small>';
					} else {
						text += ' Next damage: ' + Math.floor(100 / (clientPokemon.volatiles['dynamax'] ? 32 : 16) * Math.min(clientPokemon.statusData.toxicTurns + 1, 15)) + '%';
					}
				} else if (pokemon.status === 'slp') {
					text += ' Turns asleep: ' + clientPokemon.statusData.sleepTurns;
				}
			}
			text += '</p>';
		}

		const supportsAbilities = this.battle.gen > 2 && !this.battle.tier.includes("Let's Go");

		let abilityText = '';
		if (supportsAbilities) {
			abilityText = this.getPokemonAbilityText(
				clientPokemon, serverPokemon, isActive, !!illusionIndex && illusionIndex > 1
			);
		}

		let itemText = '';
		if (serverPokemon) {
			let item = '';
			let itemEffect = '';
			if (clientPokemon?.prevItem) {
				item = 'None';
				let prevItem = Dex.items.get(clientPokemon.prevItem).name;
				itemEffect += clientPokemon.prevItemEffect ? prevItem + ' was ' + clientPokemon.prevItemEffect : 'was ' + prevItem;
			}
			if (serverPokemon.item) item = Dex.items.get(serverPokemon.item).name;
			if (itemEffect) itemEffect = ' (' + itemEffect + ')';
			if (item) itemText = '<small>Item:</small> ' + item + itemEffect;
		} else if (clientPokemon) {
			let item = '';
			let itemEffect = clientPokemon.itemEffect || '';
			if (clientPokemon.prevItem) {
				item = 'None';
				if (itemEffect) itemEffect += '; ';
				let prevItem = Dex.items.get(clientPokemon.prevItem).name;
				itemEffect += clientPokemon.prevItemEffect ? prevItem + ' was ' + clientPokemon.prevItemEffect : 'was ' + prevItem;
			}
			if (pokemon.item) item = Dex.items.get(pokemon.item).name;
			if (itemEffect) itemEffect = ' (' + itemEffect + ')';
			if (item) itemText = '<small>Item:</small> ' + item + itemEffect;
		}

		if (abilityText || itemText) {
			text += '<p>';
			text += abilityText;
			if (abilityText && itemText) {
				// ability/item on one line for your own switch tooltips, two lines everywhere else
				text += (!isActive && serverPokemon ? ' / ' : '</p><p>');
			}
			text += itemText;
			text += '</p>';
		}


		// Owner-only request data is authoritative. Opponents use only their publicly displayed form.
		const passives = serverPokemon?.passives ?? (clientPokemon ? clientPokemon.getSpecies().passives : []);
		if (passives.length && !(illusionIndex && illusionIndex > 1)) {
			text += '<p class="pokemon-passives"><small>' + (passives.length === 1 ? 'Passive' : 'Passives') + ':</small> ' + passives.map(id =>
				'<span class="passive-tooltip-entry"><strong>' + BattleLog.escapeHTML(Dex.abilities.get(id).name) + '</strong>: ' +
				BattleLog.escapeHTML(Dex.getPassiveAbilityDetails(id).shortDesc) + '</span>'
			).join(' · ') + '<br /><small>' + (passives.includes('crystalward') ? 'Disabled by Ability suppression.' : 'Stays active if Ability is suppressed.') + '</small></p>';
		}
		if (clientPokemon?.adaptation) text += this.renderAdaptation(clientPokemon.adaptation);
		text += this.renderStats(clientPokemon, serverPokemon, !isActive);

		if (serverPokemon && !isActive) {
			// move list
			text += `<p class="tooltip-section">`;
			const battlePokemon = clientPokemon || this.battle.findCorrespondingPokemon(pokemon);
			for (const moveid of serverPokemon.moves) {
				const move = Dex.moves.get(moveid);
				let moveName = `&#8226; ${move.name}`;
				if (battlePokemon?.moveTrack) {
					for (const row of battlePokemon.moveTrack) {
						if (moveName === row[0]) {
							moveName = this.getPPUseText(row, true);
							break;
						}
					}
				}
				text += `${moveName}<br />`;
			}
			text += '</p>';
		} else if (!this.battle.hardcoreMode && clientPokemon?.moveTrack.length) {
			// move list (guessed)
			text += `<p class="tooltip-section">`;
			for (const row of clientPokemon.moveTrack) {
				text += `${this.getPPUseText(row)}<br />`;
			}
			if (clientPokemon.moveTrack.filter(([moveName]) => {
				if (moveName.charAt(0) === '*') return false;
				const move = this.battle.dex.moves.get(moveName);
				return !move.isZ && !move.isMax && move.name !== 'Mimic';
			}).length > 4) {
				text += `(More than 4 moves is usually a sign of Illusion Zoroark/Zorua.) `;
			}
			if (this.battle.gen === 3) {
				text += `(Pressure is not visible in Gen 3, so in certain situations, more PP may have been lost than shown here.) `;
			}
			if (this.pokemonHasClones(clientPokemon)) {
				text += `(Your opponent has two indistinguishable Pokémon, making it impossible for you to tell which one has which moves/ability/item.) `;
			}
			text += `</p>`;
		}
		return text;
	}

	showFieldTooltip() {
		const scene = this.battle.scene as BattleScene;
		let buf = `<table style="border: 0; border-collapse: collapse; vertical-align: top; padding: 0; width: 100%"><tr>`;

		let atLeastOne = false;
		for (const side of this.battle.sides) {
			const sideConditions = scene.sideConditionsLeft(side, true);
			if (sideConditions) atLeastOne = true;
			buf += `<td><p class="tooltip-section"><strong>${BattleLog.escapeHTML(side.name)}</strong>${sideConditions || "<br />(no conditions)"}</p></td>`;
		}
		buf += `</tr><table>`;
		if (!atLeastOne) buf = ``;

		let weatherbuf = scene.weatherLeft() || `(no weather)`;
		if (weatherbuf.startsWith('<br />')) {
			weatherbuf = weatherbuf.slice(6);
		}
		buf = `<p>${weatherbuf}</p>` + buf;
		if (this.battle.hasPseudoWeather('Midnight Zone Terrain')) {
			buf += '<p><strong>Midnight Zone</strong>: Fire, weather, and generated fields fail. Speed ×0.25 except Water, Swift Swim, Steelworker, or Schooling. Physical power ×0.33 except Water-type Pokemon or those three abilities.</p>';
			buf += '<p>Water/Ice/Dark ×1.5; Electric/Ground ×1.2. Ground, Dragon Darts, and Grav Apple become Water. Special Dark uses Water matchups; Dark attacks have a 30% Sp. Atk drop chance.</p>';
			buf += '<p>Pressure damage: 10% HP, or 25% for Steel/Ice/Fire/Rock. Water, Water Veil, Dry Skin, Storm Drain, Steelworker, Schooling, and Magic Guard are immune. Water heals 1/16; Water Absorb/Dry Skin heal 1/10.</p>';
			buf += '<p>Flash, Dazzling Gleam, Light That Burns the Sky, Bounce, and Fly return to Underwater.</p>';
		}
		return `<p>${buf}</p>`;
	}

	/**
	 * Does this Pokémon's trainer have two of these Pokémon that are
	 * indistinguishable? (Same nickname, species, forme, level, gender,
	 * and shininess.)
	 */
	pokemonHasClones(pokemon: Pokemon) {
		const side = pokemon.side;
		if (side.battle.speciesClause) return false;
		for (const ally of side.pokemon) {
			if (pokemon !== ally && pokemon.searchid === ally.searchid) {
				return true;
			}
		}
		return false;
	}

	calculateModifiedStats(clientPokemon: Pokemon | null, serverPokemon: ServerPokemon, statStagesOnly?: boolean) {
		let stats = {...serverPokemon.stats};
		let pokemon = clientPokemon || serverPokemon;
		const physicalSwap = !!clientPokemon?.volatiles['powertrick'] !== !!clientPokemon?.volatiles['powershift'];
		const specialSwap = !!clientPokemon?.volatiles['powershift']?.[1];
		for (const statName of Dex.statNamesExceptHP) {
			let sourceStatName = statName;
			if (physicalSwap) {
				if (statName === 'atk') sourceStatName = 'def';
				if (statName === 'def') sourceStatName = 'atk';
			}
			if (specialSwap) {
				if (statName === 'spa') sourceStatName = 'spd';
				if (statName === 'spd') sourceStatName = 'spa';
			}
			if (statName === 'spa' && new ModifiableValue(this.battle, clientPokemon!, serverPokemon).tryAbility('Twin Fang')) sourceStatName = physicalSwap ? 'def' : 'atk';
			stats[statName] = serverPokemon.stats[sourceStatName];
			if (!clientPokemon) continue;

			const clientStatName = clientPokemon.boosts.spc && (statName === 'spa' || statName === 'spd') ? 'spc' : statName;
			let boostLevel = clientPokemon.boosts[clientStatName] || 0;
            if ((clientPokemon.volatiles.artilleryreload || clientPokemon.volatiles.artilleryfired) && ['def', 'spd'].includes(statName) &&
                toID(clientPokemon.effectiveAbility(serverPokemon)) === 'heavyartillery') boostLevel = Math.max(-6, Math.min(6, boostLevel + (clientPokemon.volatiles.artilleryfired ? -1 : 1)));
			if (boostLevel) {
				let boostTable = [1, 1.5, 2, 2.5, 3, 3.5, 4];
				if (boostLevel > 0) {
					stats[statName] *= boostTable[boostLevel];
				} else {
					if (this.battle.gen <= 2) boostTable = [1, 100 / 66, 2, 2.5, 100 / 33, 100 / 28, 4];
					stats[statName] /= boostTable[-boostLevel];
				}
				stats[statName] = Math.floor(stats[statName]);
			}
		}
		if (statStagesOnly) return stats;

		const ability = toID(
			clientPokemon?.effectiveAbility(serverPokemon) ?? (serverPokemon.ability || serverPokemon.baseAbility)
		);

		const passiveStats = new ModifiableValue(this.battle, clientPokemon!, serverPokemon);
		if (passiveStats.tryAbility('Adaptive Cell') || passiveStats.tryAbility('Eclipse Vision')) stats.spa = Math.floor(stats.spa * 1.3);
		if (passiveStats.tryAbility('Rival Malice')) stats.spe = Math.floor(stats.spe * 1.5);
		if (passiveStats.tryAbility('Fairy Ring')) stats.spd = Math.floor(stats.spd * 1.3);
		const hasQuickFeet = this.battle.gen > 2 && new ModifiableValue(this.battle, clientPokemon!, serverPokemon).tryAbility('Quick Feet');
		if (hasQuickFeet && (pokemon.status || this.battle.hasPseudoWeather('Electric Terrain'))) {
			stats.spe = Math.floor(stats.spe * (this.battle.hasPseudoWeather('Electric Terrain') ? 2 : 1.5));
		}
		// check for burn, paralysis, guts, quick feet
		if (pokemon.status) {
			if (this.battle.gen > 2 && new ModifiableValue(this.battle, clientPokemon!, serverPokemon).tryAbility('Guts')) {
				stats.atk = Math.floor(stats.atk * 1.5);
			} else if (this.battle.gen < 2 && pokemon.status === 'brn') {
				stats.atk = Math.floor(stats.atk * 0.5);
			}

		}

		// gen 1 doesn't support items
		if (this.battle.gen <= 1) {
			for (const statName of Dex.statNamesExceptHP) {
				if (stats[statName] > 999) stats[statName] = 999;
			}
			return stats;
		}

		let item = toID(serverPokemon.item);
		let speedHalvingEVItems = ['machobrace', 'poweranklet', 'powerband', 'powerbelt', 'powerbracer', 'powerlens', 'powerweight'];
		if (
			(ability === 'klutz' && !speedHalvingEVItems.includes(item)) ||
			this.battle.hasPseudoWeather('Magic Room') ||
			clientPokemon?.volatiles['embargo']
		) {
			item = '' as ID;
		}

		const species = Dex.species.get(serverPokemon.speciesForme).baseSpecies;
		const isTransform = clientPokemon?.volatiles.transform;
		const speciesName = isTransform && clientPokemon?.volatiles.formechange?.[1] && this.battle.gen <= 4 ?
			this.battle.dex.species.get(clientPokemon.volatiles.formechange[1]).baseSpecies : species;

		let speedModifiers = [];

		// check for light ball, thick club, metal/quick powder
		// the only stat modifying items in gen 2 were light ball, thick club, metal powder
		if (item === 'lightball' && speciesName === 'Pikachu' && this.battle.gen !== 4) {
			if (this.battle.gen > 4) stats.atk *= 2;
			stats.spa *= 2;
		}

		if (item === 'thickclub') {
			if (speciesName === 'Marowak' || speciesName === 'Cubone') {
				stats.atk *= 2;
			}
		}

		if (speciesName === 'Ditto' && !(clientPokemon && 'transform' in clientPokemon.volatiles)) {
			if (item === 'quickpowder') {
				speedModifiers.push(2);
			}
			if (item === 'metalpowder') {
				if (this.battle.gen === 2) {
					stats.def = Math.floor(stats.def * 1.5);
					stats.spd = Math.floor(stats.spd * 1.5);
				} else {
					stats.def *= 2;
				}
			}
		}

		// check abilities other than Guts and Quick Feet
		// check items other than light ball, thick club, metal/quick powder
		if (this.battle.gen <= 2) {
			return stats;
		}

		const weather = new ModifiableValue(this.battle, clientPokemon!, serverPokemon).effectiveWeather();

		if (item === 'choiceband' && !clientPokemon?.volatiles['dynamax']) {
			stats.atk = Math.floor(stats.atk * 1.5);
		}
		const hasPurePower = new ModifiableValue(this.battle, clientPokemon!, serverPokemon).tryAbility('Pure Power');
  if (hasPurePower) {
   if (this.battle.hasPseudoWeather('Psychic Terrain')) stats.spa *= 2;
   else stats.atk *= 2;
  }
  if (ability === 'hugepower') stats.atk *= 2;
		if (ability === 'hustle' || (ability === 'gorillatactics' && !clientPokemon?.volatiles['dynamax'])) {
			stats.atk = Math.floor(stats.atk * 1.5);
		}
		if (weather) {
			if (this.battle.gen >= 4 && this.pokemonHasType(pokemon, 'Rock') && weather === 'sandstorm') {
				stats.spd = Math.floor(stats.spd * 1.5);
			}
			if (this.pokemonHasType(pokemon, 'Ice') && weather === 'snow') {
				stats.def = Math.floor(stats.def * 1.5);
			}
			if (ability === 'slushrush' && (weather === 'hail' || weather === 'snow')) {
				speedModifiers.push(2);
			}
			if (item !== 'utilityumbrella') {
				if (weather === 'sunnyday' || weather === 'desolateland') {
					if (ability === 'chlorophyll') {
						speedModifiers.push(2);
					}
					if (ability === 'solarpower' && !this.battle.pseudoWeather.some(([field]) => this.battle.getTerrainId(field) === 'coldeclipseterrain')) {
						stats.atk = Math.floor(stats.atk * 1.5);
						stats.spa = Math.floor(stats.spa * 1.5);
					}
					if (ability === 'orichalcumpulse') {
						stats.atk = Math.floor(stats.atk * 1.3333);
					}
					let allyActive = clientPokemon?.side.active;
					if (allyActive) {
						for (const ally of allyActive) {
							if (!ally || ally.fainted) continue;
							let allyAbility = this.getAllyAbility(ally);
							if (allyAbility === 'Flower Gift' && (ally.getSpecies().baseSpecies === 'Cherrim' || this.battle.gen <= 4)) {
								stats.atk = Math.floor(stats.atk * 1.5);
								stats.spd = Math.floor(stats.spd * 1.5);
							}
						}
					}
				}
			}
		}
		if (this.swiftSwimSpeedBoost(ability, weather, item)) speedModifiers.push(2);
		const fieldPassives = serverPokemon.passives ?? this.battle.dex.species.get(serverPokemon.speciesForme).passives;
		const exclusions = Dex.getAbilityComponentExclusions(ability, fieldPassives, serverPokemon.speciesForme);
		const fieldAbilityEffects = new Set(Array.from(Dex.getAbilityEffects(ability)).filter(effect => !exclusions.includes(effect)));
		for (const passive of fieldPassives) for (const effect of Array.from(Dex.getPassiveAbilityEffects(toID(passive)))) fieldAbilityEffects.add(effect);
		if (fieldAbilityEffects.has('monarchofice' as ID)) {
			if (['hail', 'snow', 'snowscape'].includes(weather) ||
				['Icy Terrain', 'Snowy Mountain Terrain', 'Cold Eclipse Terrain'].some(field => this.battle.hasPseudoWeather(field))) speedModifiers.push(2);
			if (weather === 'hail') stats.def = Math.floor(stats.def * (this.battle.hasPseudoWeather('Snowy Mountain Terrain') ? 2.25 : 1.5));
			if (weather === 'snowscape' || weather === 'snow' && !this.pokemonHasType(pokemon, 'Ice')) stats.def = Math.floor(stats.def * 1.5);
			if (weather === 'hail' && this.battle.hasPseudoWeather('Icy Terrain')) stats.def = Math.floor(stats.def * 1.5);
			if (weather === 'hail' && this.battle.hasPseudoWeather('Snowy Terrain')) stats.spd = Math.floor(stats.spd * 1.5);
			if (this.battle.hasPseudoWeather('Cold Eclipse Terrain')) { stats.def = Math.floor(stats.def * 1.5); stats.spd = Math.floor(stats.spd * 1.5); }
		}
		if (fieldAbilityEffects.has(toID('sandrush')) && (weather === 'sandstorm' ||
			this.battle.hasPseudoWeather('Desert Terrain') || this.battle.hasPseudoWeather('Ashen Beach Terrain'))) speedModifiers.push(2);
		if ((this.battle.hasPseudoWeather('Psychic Aura') || this.battle.hasPseudoWeather('Psychic Terrain')) &&
			fieldAbilityEffects.has(toID('telepathy'))) {
			speedModifiers.push(2);
		}
		if (this.battle.hasPseudoWeather('Misty Aura') || this.battle.hasPseudoWeather('Misty Terrain')) {
			const types = clientPokemon ? clientPokemon.getTypes(serverPokemon)[0] : this.battle.dex.species.get(serverPokemon.speciesForme).types;
			if (types.includes('Fairy')) stats.spd = Math.floor(stats.spd * 1.5);
		}

		const fieldAdapted = (name: string) => clientPokemon?.adaptation?.active &&
			clientPokemon.adaptation.fields[toID(name)]?.stage === 3;
		if (!fieldAbilityEffects.has('limber' as ID)) {
			const types = clientPokemon ? clientPokemon.getTypes(serverPokemon)[0] :
				this.battle.dex.species.get(serverPokemon.speciesForme).types;
			const passives = serverPokemon.passives ?? this.battle.dex.species.get(serverPokemon.speciesForme).passives;
			const airborneAbility = passives.includes('levitate') || passives.includes('elevate') ||
				(!Dex.getAbilityComponentExclusions(ability, passives).includes('levitate') &&
				['levitate', 'elevate', 'solaridol', 'lunaridol', 'burningcrown',
					'astralwitchcraft', 'voidcraft', 'phantombarrage'].some(id => fieldAbilityEffects.has(id as ID)));
			const grounded = clientPokemon ? clientPokemon.isGrounded(serverPokemon) :
				this.battle.hasPseudoWeather('Gravity') || item === 'ironball' ||
				(!types.includes('Flying') && !airborneAbility && item !== 'airballoon');
			const fieldSpeedRules: [string, number, boolean, string[], string[]][] = [
				['Cold Eclipse Terrain', 0.75, true, ['Ice', 'Dragon'],
					['slushrush', 'icebody', 'mindfreeze', 'thickfat', 'illusion', 'duskilate', 'armorize',
						'webassassin', 'spiralevolution']],
				['Icy Terrain', 0.75, true, ['Ice'], ['snowcloak', 'slushrush', 'icebody', 'refrigerate', 'spiralevolution']],
				['Water Surface Terrain', 0.75, true, ['Water'], ['surgesurfer', 'swiftswim', 'webassassin', 'spiralevolution']],
				['Murkwater Surface Terrain', 0.75, true, ['Water'], ['surgesurfer', 'swiftswim', 'webassassin', 'spiralevolution']],
				['New World Terrain', 0.75, true, [], ['webassassin', 'spiralevolution']],
				['Snowy Terrain', 0.75, true, ['Ice'], ['slushrush', 'icebody', 'snowcloak', 'webassassin', 'spiralevolution']],
				['Underwater Terrain', 0.5, false, ['Water'],
					['elevate', 'swiftswim', 'steelworker', 'webassassin', 'spiralevolution']],
			];
			fieldSpeedRules.forEach(([field, factor, groundedOnly, exemptTypes, exemptAbilities]) => {
				if (!fieldAdapted(field) && this.battle.hasPseudoWeather(field) && (!groundedOnly || grounded) &&
					!types.some(type => exemptTypes.includes(type)) &&
					!exemptAbilities.some(id => fieldAbilityEffects.has(id as ID))) speedModifiers.push(factor);
			});
		}
		if (this.battle.hasPseudoWeather('Midnight Zone Terrain')) {
			const types = clientPokemon ? clientPokemon.getTypes(serverPokemon)[0] : this.battle.dex.species.get(serverPokemon.speciesForme).types;
			if (!fieldAdapted('Midnight Zone Terrain') && !types.includes('Water') &&
				!['steelworker', 'schooling', 'swiftswim', 'limber'].some(id => fieldAbilityEffects.has(toID(id)))) {
				speedModifiers.push(0.25);
			}
			if (ability === 'propellertail') speedModifiers.push(2);
		}
		if (ability === 'defeatist' && serverPokemon.hp <= serverPokemon.maxhp / 4) {
			stats.atk = Math.floor(stats.atk * 0.5);
			stats.spa = Math.floor(stats.spa * 0.5);
		}
		if (clientPokemon) {
			if (clientPokemon.volatiles['slowstart']) {
				stats.atk = Math.floor(stats.atk * 0.5);
				speedModifiers.push(0.5);
			}
			if (ability === 'unburden' && clientPokemon.volatiles['itemremoved'] && !item) {
				speedModifiers.push(2);
			}
			for (const statName of Dex.statNamesExceptHP) {
				if (clientPokemon.volatiles['protosynthesis' + statName] || clientPokemon.volatiles['quarkdrive' + statName]) {
					if (statName === 'spe') {
						speedModifiers.push(1.5);
					} else {
						stats[statName] = Math.floor(stats[statName] * 1.3);
					}
				}
			}
		}
		if (ability === 'marvelscale' && pokemon.status) {
			stats.def = Math.floor(stats.def * 1.5);
		}
		const isNFE = this.battle.dex.species.get(serverPokemon.speciesForme).evos?.some(evo => {
			const evoSpecies = this.battle.dex.species.get(evo);
			return !evoSpecies.isNonstandard ||
					evoSpecies.isNonstandard === this.battle.dex.species.get(serverPokemon.speciesForme)?.isNonstandard ||
					// Pokemon with Hisui evolutions
					evoSpecies.isNonstandard === "Unobtainable";
		});
		if ((item === 'eviolite' && (isNFE || this.battle.dex.species.get(serverPokemon.speciesForme).id === 'dipplin')) ||
			(item === 'eeviumz' && ['eevee', 'eeveestarter', 'eeveestarteralt', 'eeveegmax'].includes(toID(serverPokemon.speciesForme)))) {
			stats.def = Math.floor(stats.def * 1.5);
			stats.spd = Math.floor(stats.spd * 1.5);
		}
		if (ability === 'grasspelt' && this.battle.hasPseudoWeather('Grassy Terrain')) {
			stats.def = Math.floor(stats.def * 1.5);
		}
		if (this.battle.hasPseudoWeather('Electric Terrain')) {
			if (ability === 'surgesurfer') {
				speedModifiers.push(2);
			}
			if (ability === 'hadronengine') {
				stats.spa = Math.floor(stats.spa * 1.3333);
			}
		}
		if (item === 'choicespecs' && !clientPokemon?.volatiles['dynamax']) {
			stats.spa = Math.floor(stats.spa * 1.5);
		}
		if (item === 'deepseatooth' && species === 'Clamperl') {
			stats.spa *= 2;
		}
		if (item === 'souldew' && this.battle.gen <= 6 && (species === 'Latios' || species === 'Latias')) {
			stats.spa = Math.floor(stats.spa * 1.5);
			stats.spd = Math.floor(stats.spd * 1.5);
		}
		if (clientPokemon && (ability === 'plus' || ability === 'minus')) {
			let allyActive = clientPokemon.side.active;
			if (allyActive.length > 1) {
				let abilityName = (ability === 'plus' ? 'Plus' : 'Minus');
				for (const ally of allyActive) {
					if (!ally || ally === clientPokemon || ally.fainted) continue;
					let allyAbility = this.getAllyAbility(ally);
					if (allyAbility !== 'Plus' && allyAbility !== 'Minus') continue;
					if (this.battle.gen <= 4 && allyAbility === abilityName) continue;
					stats.spa = Math.floor(stats.spa * 1.5);
					break;
				}
			}
		}
		if (item === 'assaultvest') {
			stats.spd = Math.floor(stats.spd * 1.5);
		}
		if (item === 'deepseascale' && species === 'Clamperl') {
			stats.spd *= 2;
		}
		if (item === 'choicescarf' && !clientPokemon?.volatiles['dynamax']) {
			speedModifiers.push(1.5);
		}
		if (item === 'ironball' || speedHalvingEVItems.includes(item)) {
			speedModifiers.push(0.5);
		}
		if (ability === 'furcoat') {
			stats.def *= 2;
		}
		if (clientPokemon && ['psn', 'tox'].includes(pokemon.status) && this.battle.getAllActive().some(active => {
			if (active.fainted || active.side === clientPokemon.side || active.side === clientPokemon.side.ally) return false;
			return active.getSpecies().passives.includes('venomthread') || toID(active.effectiveAbility()) === 'venomthread';
		})) speedModifiers.push(0.75);
		if (this.battle.abilityActive('Vessel of Ruin')) {
			if (ability !== 'vesselofruin') {
				stats.spa = Math.floor(stats.spa * 0.75);
			}
		}
		if (this.battle.abilityActive('Sword of Ruin')) {
			if (ability !== 'swordofruin') {
				stats.def = Math.floor(stats.def * 0.75);
			}
		}
		if (this.battle.abilityActive('Tablets of Ruin')) {
			if (ability !== 'tabletsofruin') {
				stats.atk = Math.floor(stats.atk * 0.75);
			}
		}
		if (this.battle.abilityActive('Beads of Ruin')) {
			if (ability !== 'beadsofruin') {
				stats.spd = Math.floor(stats.spd * 0.75);
			}
		}
		const sideConditions = this.battle.mySide.sideConditions;
		if (sideConditions['tailwind']) {
			speedModifiers.push(2);
		}
		if (sideConditions['grasspledge']) {
			speedModifiers.push(0.25);
		}

		let chainedSpeedModifier = 1;
		for (const modifier of speedModifiers) {
			chainedSpeedModifier *= modifier;
		}
		// Chained modifiers round down on 0.5
		stats.spe = stats.spe * chainedSpeedModifier;
		stats.spe = stats.spe % 1 > 0.5 ? Math.ceil(stats.spe) : Math.floor(stats.spe);

		if (pokemon.status === 'par' && !hasQuickFeet) {
			if (this.battle.gen > 6) {
				stats.spe = Math.floor(stats.spe * 0.5);
			} else {
				stats.spe = Math.floor(stats.spe * 0.25);
			}
		}

		return stats;
	}

	/** Mirrors verified server Speed hooks, not merely component labels. */
	swiftSwimSpeedBoost(ability: string, weather: string, item = ''): boolean {
		const fullSwiftSwim = ['swiftswim', 'swiftvenom', 'islandcurrent', 'noblerider', 'warship', 'aevianrocket',
			'swiftdrill', 'emperorsresolve', 'ragingcurrent', 'crosscurrent', 'prismscale', 'royalscales',
			'tidaljaw', 'helios', 'tidaldominion', 'currentcoil', 'stormcircuit'].includes(ability);
		const rain = item !== 'utilityumbrella' && ['raindance', 'primordialsea'].includes(weather);
		if (ability === 'riptideclaws') return rain; // This server's hook is rain-only.
		return fullSwiftSwim && (rain || ['Water Surface Terrain', 'Underwater Terrain',
			'Midnight Zone Terrain', 'Murkwater Surface Terrain'].some(field => this.battle.hasPseudoWeather(field)));
	}

	renderStats(clientPokemon: Pokemon | null, serverPokemon?: ServerPokemon | null, short?: boolean) {
		const isTransformed = clientPokemon?.volatiles.transform;
		if (!serverPokemon || isTransformed) {
			if (!clientPokemon) throw new Error('Must pass either clientPokemon or serverPokemon');
			let [min, max] = this.getSpeedRange(clientPokemon);
			let note = '';
			// Only revealed active abilities; never infer an opponent's slot from its species.
			const ability = clientPokemon.isActive() ? toID(clientPokemon.effectiveAbility()) : '';
			if (this.swiftSwimSpeedBoost(ability, '')) {
				note = '<p><small>Known field effect: Swift Swim component doubles Speed (×2). The range above is unmodified.</small></p>';
			} else {
				const weather = this.battle.abilityActive(['Air Lock', 'Cloud Nine']) ? '' : this.battle.weather;
				if (this.swiftSwimSpeedBoost(ability, weather, toID(clientPokemon.item))) {
					note = '<p><small>Swift Swim component: ×2 Speed in effective rain; unknown item effects are not included.</small></p>';
				}
			}
			return '<p><small>Spe</small> ' + min + ' to ' + max + ' <small>(before items/abilities/modifiers)</small></p>' + note;
		}
		const stats = serverPokemon.stats;
		const modifiedStats = this.calculateModifiedStats(clientPokemon, serverPokemon);

		let buf = '<p>';

		if (!short) {
			let hasModifiedStat = false;
			for (const statName of Dex.statNamesExceptHP) {
				if (this.battle.gen === 1 && statName === 'spd') continue;
				let statLabel = this.battle.gen === 1 && statName === 'spa' ? 'spc' : statName;
				buf += statName === 'atk' ? '<small>' : '<small> / ';
				buf += '' + BattleText[statLabel].statShortName + '&nbsp;</small>';
				buf += '' + stats[statName];
				if (modifiedStats[statName] !== stats[statName]) hasModifiedStat = true;
			}
			buf += '</p>';

			if (!hasModifiedStat) return buf;

			buf += '<p><small>(After stat modifiers:)</small></p>';
			buf += '<p>';
		}

		for (const statName of Dex.statNamesExceptHP) {
			if (this.battle.gen === 1 && statName === 'spd') continue;
			let statLabel = this.battle.gen === 1 && statName === 'spa' ? 'spc' : statName;
			buf += statName === 'atk' ? '<small>' : '<small> / ';
			buf += '' + BattleText[statLabel].statShortName + '&nbsp;</small>';
			if (modifiedStats[statName] === stats[statName]) {
				buf += '' + modifiedStats[statName];
			} else if (modifiedStats[statName] < stats[statName]) {
				buf += '<strong class="stat-lowered">' + modifiedStats[statName] + '</strong>';
			} else {
				buf += '<strong class="stat-boosted">' + modifiedStats[statName] + '</strong>';
			}
		}
		buf += '</p>';
		return buf;
	}

	getPPUseText(moveTrackRow: [string, number], showKnown?: boolean) {
		let [moveName, ppUsed] = moveTrackRow;
		let move;
		let maxpp;
		if (moveName.charAt(0) === '*') {
			// Transformed move
			move = this.battle.dex.moves.get(moveName.substr(1));
			maxpp = 5;
		} else {
			move = this.battle.dex.moves.get(moveName);
			maxpp = (move.pp === 1 || move.noPPBoosts ? move.pp : move.pp * 8 / 5);
			if (this.battle.gen < 3) maxpp = Math.min(61, maxpp);
		}
		const bullet = moveName.charAt(0) === '*' || move.isZ ? '<span style="color:#888">&#8226;</span>' : '&#8226;';
		if (ppUsed === Infinity) {
			return `${bullet} ${move.name} <small>(0/${maxpp})</small>`;
		}
		if (ppUsed || moveName.charAt(0) === '*') {
			return `${bullet} ${move.name} <small>(${maxpp - ppUsed}/${maxpp})</small>`;
		}
		return `${bullet} ${move.name} ${showKnown ? ' <small>(revealed)</small>' : ''}`;
	}

	ppUsed(move: Move, pokemon: Pokemon) {
		for (let [moveName, ppUsed] of pokemon.moveTrack) {
			if (moveName.charAt(0) === '*') moveName = moveName.substr(1);
			if (move.name === moveName) return ppUsed;
		}
		return 0;
	}

	/**
	 * Calculates possible Speed stat range of an opponent
	 */
	getSpeedRange(pokemon: Pokemon): [number, number] {
		const tr = Math.trunc || Math.floor;
		const species = pokemon.getSpecies();
		let rules = this.battle.rules;
		let baseSpe = species.baseStats.spe;
		if (rules['Scalemons Mod']) {
			const bstWithoutHp = species.bst - species.baseStats.hp;
			const scale = 600 - species.baseStats.hp;
			baseSpe = tr(baseSpe * scale / bstWithoutHp);
			if (baseSpe < 1) baseSpe = 1;
			if (baseSpe > 255) baseSpe = 255;
		}
		if (rules['Frantic Fusions Mod']) {
			const fusionSpecies = this.battle.dex.species.get(pokemon.name);
			if (fusionSpecies.exists && fusionSpecies.name !== species.name) {
				baseSpe = baseSpe + tr(fusionSpecies.baseStats.spe / 4);
				if (baseSpe < 1) baseSpe = 1;
				if (baseSpe > 255) baseSpe = 255;
			}
		}
		if (rules['Flipped Mod']) {
			baseSpe = species.baseStats.hp;
			if (baseSpe < 1) baseSpe = 1;
			if (baseSpe > 255) baseSpe = 255;
		}
		if (rules['350 Cup Mod'] && species.bst <= 350) {
			baseSpe *= 2;
			if (baseSpe < 1) baseSpe = 1;
			if (baseSpe > 255) baseSpe = 255;
		}
		let level = pokemon.volatiles.transform?.[4] || pokemon.level;
		let tier = this.battle.tier;
		let gen = this.battle.gen;
		let isCGT = tier.includes('Computer-Generated Teams');
		let isRandomBattle = tier.includes('Random Battle') ||
			(tier.includes('Random') && tier.includes('Battle') && gen >= 6) || isCGT;

		let minNature = (isRandomBattle || gen < 3) ? 1 : 0.9;
		let maxNature = (isRandomBattle || gen < 3) ? 1 : 1.1;
		let maxIv = (gen < 3) ? 30 : 31;

		let min;
		let max;
		if (tier.includes("Let's Go")) {
			min = tr(tr(tr(2 * baseSpe * level / 100 + 5) * minNature) * tr((70 / 255 / 10 + 1) * 100) / 100);
			max = tr(tr(tr((2 * baseSpe + maxIv) * level / 100 + 5) * maxNature) * tr((70 / 255 / 10 + 1) * 100) / 100);
			if (tier.includes('No Restrictions')) max += 200;
			else if (tier.includes('Random')) max += 20;
		} else {
			let maxIvEvOffset = maxIv + ((isRandomBattle && gen >= 3) ? 21 : 63);
			max = tr(tr((2 * baseSpe + maxIvEvOffset) * level / 100 + 5) * maxNature);
			min = isCGT ? max : tr(tr(2 * baseSpe * level / 100 + 5) * minNature);
		}
		return [min, max];
	}

	/**
	 * Gets the proper current type for moves with a variable type.
	 */
	getMoveType(move: Move, value: ModifiableValue, forMaxMove?: boolean | Move): [TypeName, 'Physical' | 'Special' | 'Status'] {
		const pokemon = value.pokemon;
		const serverPokemon = value.serverPokemon;

		let moveType = move.type;
		let category = move.category;
		if (category === 'Status' && forMaxMove) return ['Normal', 'Status']; // Max Guard
		// can happen in obscure situations
		if (!pokemon) return [moveType, category];

		let pokemonTypes = pokemon.getTypeList(serverPokemon);
		value.reset();
		if (move.id === 'thrash' && value.tryAbility('Raging Heart')) moveType = 'Ghost';
		if (move.id === 'revelationdance') {
			moveType = pokemonTypes[0];
		}
		if (move.id === 'vorpalblade' && this.battle.hasPseudoWeather('Glitch Terrain')) moveType = '???';
		if (['gildedarrow', 'gildedhelix'].includes(move.id)) {
			const types = pokemon.getTypes(serverPokemon)[0];
			moveType = types[1] && !['Fairy', 'Dark'].includes(types[1]) ? types[1] : types[0];
		}
		// Moves that require an item to change their type.
		let item = Dex.items.get(value.itemName);
		if (move.id === 'multiattack' && item.onMemory) {
			if (value.itemModify(0)) moveType = item.onMemory;
		}
		if ((move.id === 'judgment' && !item.zMoveType || move.id === 'multipulse' && item.id.endsWith('plate')) && item.onPlate) {
			if (value.itemModify(0)) moveType = item.onPlate;
		}
		if (move.id === 'technoblast' && item.onDrive) {
			if (value.itemModify(0)) moveType = item.onDrive;
		}
		if (move.id === 'naturalgift' && item.naturalGift) {
			if (value.itemModify(0)) moveType = item.naturalGift.type;
		}
		// Weather and pseudo-weather type changes.
		if (move.id === 'weatherball' && value.weatherModify(0)) {
			const weather = value.effectiveWeather();
			if (weather === 'deltastream') moveType = 'Flying';
			switch (weather) {
			case 'sunnyday':
			case 'desolateland':
				moveType = 'Fire';
				break;
			case 'raindance':
			case 'primordialsea':
				moveType = 'Water';
				break;
			case 'sandstorm':
				moveType = 'Rock';
				break;
			case 'hail':
			case 'snow':
				moveType = 'Ice';
				break;
			}
		}
		if (move.id === 'terrainpulse' && pokemon.isGrounded(serverPokemon)) {
			if (this.battle.hasPseudoWeather('Electric Terrain')) {
				moveType = 'Electric';
			} else if (this.battle.hasPseudoWeather('Grassy Terrain')) {
				moveType = 'Grass';
			} else if (this.battle.hasPseudoWeather('Misty Terrain')) {
				moveType = 'Fairy';
			} else if (this.battle.hasPseudoWeather('Psychic Terrain')) {
				moveType = 'Psychic';
			}
		}
		if (move.id === 'terablast' && pokemon.terastallized) {
			moveType = pokemon.terastallized as TypeName;
		}
		if (move.id === 'terastarstorm' && pokemon.getSpeciesForme() === 'Terapagos-Stellar') {
			moveType = 'Stellar';
		}

		// Aura Wheel as Morpeko-Hangry changes the type to Dark
		if (move.id === 'aurawheel' && pokemon.getSpeciesForme() === 'Morpeko-Hangry') {
			moveType = 'Dark';
		}
		// Raging Bull's type depends on the Tauros forme
		if (move.id === 'ragingbull') {
			switch (pokemon.getSpeciesForme()) {
			case 'Tauros-Paldea-Combat':
				moveType = 'Fighting';
				break;
			case 'Tauros-Paldea-Blaze':
				moveType = 'Fire';
				break;
			case 'Tauros-Paldea-Aqua':
				moveType = 'Water';
				break;
			}
		}
		// Ivy Cudgel's type depends on the Ogerpon forme
		if (move.id === 'ivycudgel') {
			switch (pokemon.getSpeciesForme()) {
			case 'Ogerpon-Wellspring': case 'Ogerpon-Wellspring-Tera':
				moveType = 'Water';
				break;
			case 'Ogerpon-Hearthflame': case 'Ogerpon-Hearthflame-Tera':
				moveType = 'Fire';
				break;
			case 'Ogerpon-Cornerstone': case 'Ogerpon-Cornerstone-Tera':
				moveType = 'Rock';
				break;
			}
		}

		// Other abilities that change the move type.
		const noTypeOverride = [
			'judgment', 'multiattack', 'naturalgift', 'revelationdance', 'struggle', 'technoblast', 'terrainpulse', 'weatherball',
		];
		const allowTypeOverride = !noTypeOverride.includes(move.id) && (move.id !== 'terablast' || !pokemon.terastallized);
		if (allowTypeOverride) {
			if (this.battle.rules['Revelationmons Mod']) {
				const [types] = pokemon.getTypes(serverPokemon);
				for (let i = 0; i < types.length; i++) {
					if (serverPokemon.moves[i] && move.id === toID(serverPokemon.moves[i])) {
						moveType = types[i];
					}
				}
			}

			if (category !== 'Status' && !move.isZ && !move.id.startsWith('hiddenpower')) {
				if (moveType === 'Normal') {
					if (value.abilityModify(0, 'Aerilate')) moveType = 'Flying';
					if (value.abilityModify(0, 'Galvanize')) moveType = 'Electric';
					if (value.abilityModify(0, 'Pixilate')) moveType = 'Fairy';
					if (value.abilityModify(0, 'Refrigerate')) moveType = 'Ice';
					if (value.abilityModify(0, 'Rimebreaker')) moveType = 'Ice';
				}
				if (value.abilityModify(0, 'Normalize')) moveType = 'Normal';
				if (moveType === 'Normal' && value.abilityModify(0, 'Seasonal Stride')) moveType = pokemon.getTypes(value.serverPokemon)[0][0];
			}

			// There aren't any max moves with the sound flag, but if there were, Liquid Voice would make them water type
			const isSound = !!(
				forMaxMove ?
				this.getMaxMoveFromType(moveType, forMaxMove !== true && forMaxMove || undefined) : move
			).flags['sound'];
			if (isSound && move.category !== 'Status' && moveType === 'Normal' && value.abilityModify(0, 'Venom Canticle')) moveType = 'Poison';
			if (isSound && (value.abilityModify(0, 'Liquid Voice') || value.abilityModify(0, 'Storm Calling'))) {
				moveType = this.battle.hasPseudoWeather('Icy Terrain') ? 'Ice' : 'Water';
			} else if (isSound && value.abilityModify(0, 'Tidal Voice')) {
				moveType = this.battle.hasPseudoWeather('Icy Terrain') ? 'Ice' : 'Water';
			}
		}

		if (!forMaxMove && !move.isZ && !move.isMax && move.category !== 'Status' && (move.flags.pulse || move.flags.bullet) && value.abilityModify(0, 'Quarry Cannon')) category = 'Physical';
		const physicalTie = [
			'needlegun', 'dragondarts', 'gmaxspiritvolley', 'gmaxfinalverdict', 'barrage', 'explosion',
			'selfdestruct',
		].includes(move.id);
		if (!forMaxMove && !move.isZ && !move.isMax && move.id === 'wildcharge' && pokemon.volatiles.stormcharge && value.tryAbility('Storm Memory')) {
			const stats = this.calculateModifiedStats(pokemon, serverPokemon, true);
			category = stats.atk >= stats.spa ? 'Physical' : 'Special';
		}
		const specialTie = [
			'swift', 'venamskiss', 'firepledge', 'waterpledge', 'grasspledge',
			'photongeyser', 'lightthatburnsthesky', 'radiantassault', 'roaroftime', 'spacialrend',
			'shadowforce', 'veeveevolley', 'blastburn', 'frenzyplant', 'hydrocannon', 'snipeshot',
			'watershuriken', 'kaboom', 'corrosivestrike', 'spectralscream', 'barbedweb', 'bunrakubeatdown', 'lastrespects', 'sheercold', 'queensgambit', 'winddance', 'timeblade', 'crestcleave', 'swarmsnare', 'wisteriamaelstrom', 'airstreamassault', 'dragonrend', 'sirenswrath',
		].includes(move.id);
		if (physicalTie || specialTie || (move.id === 'aurasphere' && value.tryAbility('Aura Sight')) || move.id === 'terablast' && pokemon.terastallized ||
			move.id === 'terastarstorm' && pokemon.terastallized && pokemon.speciesForme === 'Terapagos-Stellar') {
			const stats = this.calculateModifiedStats(pokemon, serverPokemon, true);
			category = stats.atk > stats.spa || (physicalTie && stats.atk === stats.spa) ? 'Physical' : 'Special';
		}
		if (moveType === 'Grass' && category !== 'Status' && value.tryAbility('Canopy Mastery')) {
			const stats = this.calculateModifiedStats(pokemon, serverPokemon, true);
			category = stats.atk >= stats.spa ? 'Physical' : 'Special';
		}
		if (!forMaxMove && move.id === 'dive' && value.tryAbility('Dive Fisher')) category = 'Special';
		if (!forMaxMove && move.id === 'hyperbeam' && value.tryAbility("Champion's Command")) { moveType = 'Normal'; category = 'Physical'; }
		if (!forMaxMove && category !== 'Status' && move.flags['sound'] && value.abilityModify(0, 'Primal Rhythm')) {
			category = 'Physical';
		}
		const fieldPreview = (window as any).BattleFieldTooltips?.preview(this.battle, {...move, type: moveType, category}, pokemon, serverPokemon);
		if (fieldPreview) {
			moveType = fieldPreview.result.move.type;
			category = fieldPreview.result.move.category;
		}
		return [moveType, category];
	}

	// Gets the current accuracy for a move.
	getMoveAccuracy(move: Move, value: ModifiableValue, target?: Pokemon) {
		if (['dragonrush', 'stoneedge'].includes(move.id) && value.tryAbility('Pyrous Dominion')) move = {...move, accuracy: 100};
		value.reset(move.accuracy === true ? 0 : move.accuracy, true);
		const fieldAccuracy = (window as any).BattleFieldTooltips?.preview(this.battle, move, value.pokemon, value.serverPokemon, target);
		if (fieldAccuracy && fieldAccuracy.result.accuracy !== move.accuracy) {
			value.set(fieldAccuracy.result.accuracy === true ? 0 : fieldAccuracy.result.accuracy, fieldAccuracy.field.name);
			if (fieldAccuracy.result.accuracy === true) return value;
		}

		let pokemon = value.pokemon!;
		// Sure-hit accuracy
		if (move.id === 'toxic' && this.battle.gen >= 6 && this.pokemonHasType(pokemon, 'Poison')) {
			value.set(0, "Poison type");
			return value;
		}
		if (move.id === 'blizzard' && this.battle.gen >= 4) {
			value.weatherModify(0, 'Hail');
			value.weatherModify(0, 'Snow');
		}
		if (['hurricane', 'thunder', 'bleakwindstorm', 'wildboltstorm', 'sandsearstorm'].includes(move.id)) {
			value.weatherModify(0, 'Rain Dance');
			value.weatherModify(0, 'Primordial Sea');
		}
		value.abilityModify(0, 'No Guard');
		if (this.battle.hasPseudoWeather('Midnight Zone Terrain')) value.abilityModify(0, 'Illuminate');
		if (!value.value) return value;

		// OHKO moves don't use standard accuracy / evasion modifiers
		if (move.ohko) {
			if (this.battle.gen === 1) {
				value.set(value.value, `fails if target's Speed is higher`);
				return value;
			}
			if (move.id === 'sheercold' && this.battle.gen >= 7 && !this.pokemonHasType(pokemon, 'Ice')) {
				value.set(20, 'not Ice-type');
			}
			if (target) {
				if (pokemon.level < target.level) {
					value.reset(0);
					value.set(0, "FAILS: target's level is higher");
				} else if (pokemon.level > target.level) {
					value.set(value.value + pokemon.level - target.level, "+1% per level above target");
				}
			} else {
				if (pokemon.level < 100) value.set(value.value, "fails if target's level is higher");
				if (pokemon.level > 1) value.set(value.value, "+1% per level above target");
			}
			return value;
		}

		// Accuracy modifiers start

		let accuracyModifiers = [];
		if (this.battle.hasPseudoWeather('Gravity')) {
			accuracyModifiers.push(6840);
			value.modify(5 / 3, "Gravity");
		}

		for (const active of pokemon.side.active) {
			if (!active || active.fainted) continue;
			const ability = this.getAllyAbility(active);
			if (ability === 'Victory Star') {
				accuracyModifiers.push(4506);
				value.modify(1.1, "Victory Star");
			}
		}

		if (value.tryAbility('Hustle') && move.category === 'Physical') {
			accuracyModifiers.push(3277);
			value.abilityModify(0.8, "Hustle");
		} else if (value.tryAbility('Compound Eyes')) {
			accuracyModifiers.push(5325);
			value.abilityModify(1.3, "Compound Eyes");
		}

		if (value.tryItem('Wide Lens')) {
			accuracyModifiers.push(4505);
			value.itemModify(1.1, "Wide Lens");
		}

		// Chaining modifiers
		let chain = 4096;
		for (const mod of accuracyModifiers) {
			if (mod !== 4096) {
				chain = (chain * mod + 2048) >> 12;
			}
		}

		// Applying modifiers
		value.set(move.accuracy as number);

		if (move.id === 'hurricane' || move.id === 'thunder') {
			if (value.tryWeather('Sunny Day')) value.set(50, 'Sunny Day');
			if (value.tryWeather('Desolate Land')) value.set(50, 'Desolate Land');
		}

		// Chained modifiers round down on 0.5
		let accuracyAfterChain = (value.value * chain) / 4096;
		accuracyAfterChain = accuracyAfterChain % 1 > 0.5 ? Math.ceil(accuracyAfterChain) : Math.floor(accuracyAfterChain);
		value.set(accuracyAfterChain);

		// Unlike for Atk, Def, etc. accuracy and evasion boosts are applied after modifiers
		if (pokemon?.boosts.accuracy) {
			if (pokemon.boosts.accuracy > 0) {
				value.set(Math.floor(value.value * (pokemon.boosts.accuracy + 3) / 3));
			} else {
				value.set(Math.floor(value.value * 3 / (3 - pokemon.boosts.accuracy)));
			}
		}

		// 1/256 glitch
		if (this.battle.gen === 1 && !toID(this.battle.tier).includes('stadium')) {
			value.set((Math.floor(value.value * 255 / 100) / 256) * 100);
		}
		return value;
	}

	// Gets the proper current base power for moves which have a variable base power.
	// Takes into account the target for some moves.
	// If it is unsure of the actual base power, it gives an estimate.
	getMoveBasePower(move: Move, moveType: TypeName, value: ModifiableValue, target: Pokemon | null = null, hit = 1) {
		const pokemon = value.pokemon!;
		const serverPokemon = value.serverPokemon;

		if (!move.isZ && !move.isMax && move.category !== 'Status' && (move.flags.pulse || move.flags.bullet) && value.tryAbility('Quarry Cannon')) move = {...move, category: 'Physical'};
		// apply modifiers for moves that depend on the actual stats
		const modifiedStats = this.calculateModifiedStats(pokemon, serverPokemon);

		// Adaptive signatures select category from staged stats before field previews.
		if (['swift', 'kaboom', 'corrosivestrike', 'spectralscream', 'barbedweb', 'bunrakubeatdown', 'lastrespects', 'sheercold', 'queensgambit', 'winddance', 'timeblade', 'crestcleave', 'swarmsnare',
			'wisteriamaelstrom', 'airstreamassault', 'dragonrend', 'sirenswrath'].includes(move.id) || (move.id === 'aurasphere' && value.tryAbility('Aura Sight'))) {
			const staged = this.calculateModifiedStats(pokemon, serverPokemon, true);
			move = {...move, category: staged.atk > staged.spa ? 'Physical' : 'Special'};
		}
		const fieldPower = (window as any).BattleFieldTooltips?.preview(this.battle,
			{...move, type: moveType, midnightGround: move.type === 'Ground' && this.battle.hasPseudoWeather('Midnight Zone Terrain')},
			pokemon, serverPokemon, target);
		if (fieldPower) move = {...move, category: fieldPower.result.move.category};
		value.reset(fieldPower ? fieldPower.result.move.basePower : move.basePower);
	 const pairPreview = Dex.getDrillPowerProfile(
			{...move, type: moveType}, name => value.tryAbility(name), '', this.battle.gameType
		);
	 value.dualWieldPower = !!pairPreview?.dual;
	 value.dualWieldHit = hit;
	 value.dualWieldFull = !!pairPreview?.full;

		if (move.id === 'acrobatics') {
			if (!serverPokemon.item) {
				value.modify(2, "Acrobatics + no item");
			}
		}
		let variableBPCap = ['crushgrip', 'wringout'].includes(move.id) ? 120 : move.id === 'hardpress' ? 100 : undefined;
		if (variableBPCap && target) {
			value.set(
				Math.floor(
					Math.floor((variableBPCap * (100 * Math.floor(target.hp * 4096 / target.maxhp)) + 2048 - 1) / 4096) / 100
				) || 1,
				'approximate'
			);
		}
		if (move.id === 'terablast' && pokemon.terastallized === 'Stellar') {
			value.set(100, 'Tera Stellar boost');
		}
		if (move.id === 'brine' && target && target.hp * 2 <= target.maxhp) {
			value.modify(2, 'Brine + target below half HP');
		}
		if (move.id === 'eruption' || move.id === 'waterspout' || move.id === 'dragonenergy') {
			value.set(Math.floor(150 * pokemon.hp / pokemon.maxhp) || 1);
		}
		if (move.id === 'facade' && !['', 'slp', 'frz'].includes(pokemon.status)) {
			value.modify(2, 'Facade + status');
		}
		if (move.id === 'flail' || move.id === 'reversal') {
			let multiplier;
			let ratios;
			if (this.battle.gen > 4) {
				multiplier = 48;
				ratios = [2, 5, 10, 17, 33];
			} else {
				multiplier = 64;
				ratios = [2, 6, 13, 22, 43];
			}
			let ratio = pokemon.hp * multiplier / pokemon.maxhp;
			let basePower;
			if (ratio < ratios[0]) basePower = 200;
			else if (ratio < ratios[1]) basePower = 150;
			else if (ratio < ratios[2]) basePower = 100;
			else if (ratio < ratios[3]) basePower = 80;
			else if (ratio < ratios[4]) basePower = 40;
			else basePower = 20;
			value.set(basePower);
		}
		if (['hex', 'infernalparade', 'irritation'].includes(move.id) && target?.status) {
			value.modify(2, move.name + ' + status');
		}
		if (move.id === 'multiattack' && value.tryAbility('Adaptive Arsenal')) {
			value.powerAbilityModify(moveType === '???' ? 1.8 : 1.2, 'Adaptive Arsenal');
		}
		if (move.id === 'bunrakubeatdown') {
			value.set(Math.min(140, 80 + 15 * pokemon.side.pokemon.filter(p => p.fainted && p !== pokemon).length));
		}
		if (move.id === 'feverpitch') value.setRange(60, 100);
		if (['furycutter', 'stackingshot'].includes(move.id)) value.setRange(40, 160);
		if (move.id === 'lastrespects') {
			value.set(Math.min(140, 50 + 20 * pokemon.side.pokemon.filter(p => p.fainted && p !== pokemon).length));
		}
		if (move.id === 'punishment' && target) {
			let boostCount = 0;
			for (const boost of Object.values(target.boosts)) {
				if (boost > 0) boostCount += boost;
			}
			value.set(Math.min(60 + 20 * boostCount, 200));
		}
		if (move.id === 'smellingsalts' && target) {
			if (target.status === 'par') {
				value.modify(2, 'Smelling Salts + Paralysis');
			}
		}
		if (['storedpower', 'powertrip'].includes(move.id) && target) {
			let boostCount = 0;
			for (const boost of Object.values(pokemon.boosts)) {
				if (boost > 0) boostCount += boost;
			}
			value.set(20 + 20 * boostCount);
		}
		if (move.id === 'trumpcard') {
			const ppLeft = 5 - this.ppUsed(move, pokemon);
			let basePower = 40;
			if (ppLeft === 1) basePower = 200;
			else if (ppLeft === 2) basePower = 80;
			else if (ppLeft === 3) basePower = 60;
			else if (ppLeft === 4) basePower = 50;
			value.set(basePower);
		}
		if (move.id === 'magnitude') {
			value.setRange(10, 150);
		}
		if (['venoshock', 'barbbarrage'].includes(move.id) && target) {
			if (['psn', 'tox'].includes(target.status)) {
				value.modify(2, move.name + ' + Poison');
			}
		}
		if (move.id === 'wakeupslap' && target) {
			if (target.status === 'slp') {
				value.modify(2, 'Wake-Up Slap + Sleep');
			}
		}
		if (move.id === 'weatherball') value.weatherModify(2);
		if (move.id === 'hydrosteam') {
			value.weatherModify(1.5, 'Sunny Day');
		}
		if (!fieldPower && move.id === 'psyblade' && this.battle.hasPseudoWeather('Electric Terrain')) {
			value.modify(1.5, 'Electric Terrain');
		}
		if (!fieldPower && move.id === 'terrainpulse' && pokemon.isGrounded(serverPokemon)) {
			if (
				this.battle.hasPseudoWeather('Electric Terrain') ||
				this.battle.hasPseudoWeather('Grassy Terrain') ||
				this.battle.hasPseudoWeather('Misty Terrain') ||
				this.battle.hasPseudoWeather('Psychic Terrain')
			) {
				value.modify(2, 'Terrain Pulse boost');
			}
		}
		if (
			move.id === 'watershuriken' && pokemon.getSpeciesForme() === 'Greninja-Ash' && pokemon.ability === 'Battle Bond'
		) {
			value.set(20, 'Battle Bond');
		}
		// Moves that check opponent speed
		if (move.id === 'electroball' && target) {
			let [minSpe, maxSpe] = this.getSpeedRange(target);
			let minRatio = (modifiedStats.spe / maxSpe);
			let maxRatio = (modifiedStats.spe / minSpe);
			let min;
			let max;

			if (minRatio >= 4) min = 150;
			else if (minRatio >= 3) min = 120;
			else if (minRatio >= 2) min = 80;
			else if (minRatio >= 1) min = 60;
			else min = 40;

			if (maxRatio >= 4) max = 150;
			else if (maxRatio >= 3) max = 120;
			else if (maxRatio >= 2) max = 80;
			else if (maxRatio >= 1) max = 60;
			else max = 40;

			value.setRange(min, max);
		}
		if (move.id === 'gyroball' && target) {
			let [minSpe, maxSpe] = this.getSpeedRange(target);
			let min = (Math.floor(25 * minSpe / modifiedStats.spe) || 1);
			if (min > 150) min = 150;
			let max = (Math.floor(25 * maxSpe / modifiedStats.spe) || 1);
			if (max > 150) max = 150;
			value.setRange(min, max);
		}
		// Moves which have base power changed due to items
		if (serverPokemon.item) {
			let item = Dex.items.get(serverPokemon.item);
			if (move.id === 'fling' && item.fling && value.tryItem(item.name)) {
				value.set(item.fling.basePower, item.name);
			}
			if (move.id === 'naturalgift' && item.naturalGift && value.tryItem(item.name)) {
				value.set(item.naturalGift.basePower, item.name);
			}
		}
		// Moves which have base power changed according to weight
		if (['lowkick', 'grassknot', 'heavyslam', 'heatcrash'].includes(move.id) && this.battle.gen > 2) {
			let isGKLK = ['lowkick', 'grassknot'].includes(move.id);
			if (target) {
				let targetWeight = target.getWeightKg();
				let pokemonWeight = pokemon.getWeightKg(serverPokemon);
				let basePower;
				if (isGKLK) {
					basePower = 20;
					if (targetWeight >= 200) basePower = 120;
					else if (targetWeight >= 100) basePower = 100;
					else if (targetWeight >= 50) basePower = 80;
					else if (targetWeight >= 25) basePower = 60;
					else if (targetWeight >= 10) basePower = 40;
				} else {
					basePower = 40;
					if (pokemonWeight >= targetWeight * 5) basePower = 120;
					else if (pokemonWeight >= targetWeight * 4) basePower = 100;
					else if (pokemonWeight >= targetWeight * 3) basePower = 80;
					else if (pokemonWeight >= targetWeight * 2) basePower = 60;
				}
				if (target.volatiles['dynamax']) {
					value.set(0, 'blocked by target\'s Dynamax');
				} else {
					value.set(basePower);
				}
			} else {
				value.setRange(isGKLK ? 20 : 40, 120);
			}
		}
		// Base power based on times hit
		if (move.id === 'ragefist') {
			const bonus = serverPokemon.rageFistBonus ?? pokemon.rageFistBonus;
			value.set(50 + bonus, bonus ? `Accumulated bonus: ${bonus}; halved on switching` : undefined);
		}
		if (!value.value) return value;

		if (Dex.getSilkenMoveProfile(move, pokemon.effectiveAbility(serverPokemon), serverPokemon.item)) {
			value.value = Math.max(1, Math.ceil(value.value * 0.4 - 0.5));
			if (value.maxValue) value.maxValue = Math.max(1, Math.ceil(value.maxValue * 0.4 - 0.5));
			value.comment.push(' (Silken Salvo: per-hit power)');
		}
		const drillField = this.battle.pseudoWeather.map(([id]) => this.battle.getTerrainId(id)).find(id => id.endsWith('terrain')) || '';
  const drillPassives = serverPokemon.passives ?? pokemon.getSpecies(serverPokemon).passives;
  const drillActive = toID(pokemon.effectiveAbility(serverPokemon));
  const drillExcluded = Dex.getAbilityComponentExclusions(drillActive, drillPassives, toID(serverPokemon.speciesForme));
  const drillCopies = drillPassives.filter(id => Dex.getPassiveAbilityEffects(toID(id)).has('powerdrill' as ID)).length +
   (drillActive !== 'hyperdrill' && !drillExcluded.includes('powerdrill' as ID) && Dex.getAbilityEffects(drillActive).has('powerdrill' as ID) ? 1 : 0);
  const drillProfile = Dex.getDrillPowerProfile({...move, type: moveType}, name => value.tryAbility(name), drillField, this.battle.gameType, drillCopies);
	 if (drillProfile) {
		if (drillProfile.dual) value.powerAbilityModify(drillProfile.boost, drillProfile.label);
		else value.modify(drillProfile.hits[0], drillProfile.label);
	}
		if (moveType === 'Poison') value.abilityModify(1.3, 'Caustic Ooze');
		if (moveType === 'Grass' && ['raindance','primordialsea'].includes(this.battle.weather) && value.tryWeather()) value.abilityModify(1.3, 'Rainlit Growth');
		// Other ability boosts
		if (['dragondarts', 'gmaxspiritvolley'].includes(move.id)) {
			value.abilityModify(1.2, 'Phantom Barrage');
		}
		if (moveType === 'Fire' || move.category === 'Special' && !this.battle.hasPseudoWeather('Cold Eclipse Terrain') && (pokemon.status === 'brn' || this.battle.hasPseudoWeather('Burning Terrain') || this.battle.hasPseudoWeather('Volcanic Terrain'))) {
			value.abilityModify(1.5, "Flare Boost");
		}
		if (this.battle.hasPseudoWeather('Chessboard Terrain') && value.tryAbility('Competitive')) {
			value.abilityModify(Math.max(1, Math.min((1 - serverPokemon.hp / serverPokemon.maxhp) / 0.8 + 1, 2)), 'Competitive');
		}
		if (move.flags['punch']) {
			value.abilityModify(1.4, 'Iron Fist');
		}
		if (!drillProfile && (move.flags['pulse'] || move.flags['bullet'])) {
			value.abilityModify(1.5, "Mega Launcher");
		}
		if (move.flags['bite']) {
			value.abilityModify(1.5, "Strong Jaw");
			value.abilityModify(1.5, "Frozen Feast");
		}
		if (value.value <= value.technicianThreshold()) {
			value.abilityModify(1.5, "Technician");
		}
		if ((move.flags as any).kick && !value.abilityModify(1.4, 'Striker')) value.abilityModify(1.4, 'Long Stride');
		if (moveType === 'Grass' || move.flags.slicing) value.abilityModify(1.3, 'Canopy Mastery');
		if ((move.flags as any).kick) value.abilityModify(1.3, 'Blaze Mastery');
		if (moveType === 'Psychic') value.abilityModify(1.2, 'Linked Minds');
		if (moveType === 'Electric' && pokemon.volatiles.stormcharge) value.abilityModify(1.5, 'Storm Memory');
		if (['Water', 'Ice'].includes(moveType) && pokemon.side.sideConditions.auroraveil) value.abilityModify(1.3, 'Aurora Resonance');
		if (move.id === 'weatherball') value.abilityModify(1.3, 'Weather Weave');
		if (['rapidspin', 'mortalspin', 'icespinner', 'gyroball', 'tripleaxel'].includes(move.id)) {
			value.abilityModify(1.2, 'Cyclone Rhythm');
		}
		if (move.flags['tail'] || (move.flags as any)['tailmove']) value.abilityModify(1.5, 'Whiplash');
		if (!move.isZ && !move.isMax && ['bodyslam', 'heavyslam'].includes(move.id)) value.abilityModify(1.5, 'Leviathan Wake');
		if ((move.flags as any)['kick']) value.abilityModify(1.4, 'Seasonal Stride');
		if (move.type === 'Normal' && moveType !== 'Normal') value.abilityModify(1.2, 'Seasonal Stride');
		if (['psn', 'tox'].includes(pokemon.status) && move.category === 'Physical') {
			value.abilityModify(1.5, "Toxic Boost");
		}
		if (['Rock', 'Ground', 'Steel'].includes(moveType) && this.battle.weather === 'sandstorm') {
			if (value.tryAbility("Sand Force")) value.weatherModify(1.3, "Sandstorm", "Sand Force");
		}
		if (move.secondaries?.length || (move as any).hasSheerForceBoost) {
			value.abilityModify(1.3, "Sheer Force");
		}
		if (move.flags['contact']) {
			value.abilityModify(1.3, "Tough Claws");
		}
		if (move.category !== 'Status' && toID(pokemon.effectiveAbility(serverPokemon)) === 'atrocity') {
			value.powerAbilityModify(1.3, 'Atrocity');
			if (move.id === 'dragonrush') value.powerAbilityModify(1.5, 'Atrocity');
		}
		if (move.category !== 'Status' && moveType === 'Flying' && toID(pokemon.getSpeciesForme()) === 'castformwindy') value.abilityModify(1.3, 'Perfect Forecast');
		if (this.battle.hasPseudoWeather('Dragon’s Den Terrain') || this.battle.hasPseudoWeather("Dragon's Den Terrain") || this.battle.hasPseudoWeather('Dragons Den Terrain')) {
			if (value.tryAbility('Hydra Bond') || Dex.getAbilityEffects(toID(pokemon.effectiveAbility(serverPokemon))).has('hydrabond' as ID)) {
				value.powerAbilityModify(1.2, 'Hydra Bond');
			}
		}
		if (move.flags['sound']) {
			value.abilityModify(1.3, "Punk Rock");
			const tidalBoosted = value.abilityModify(1.2, 'Tidal Voice');
			if (move.category !== 'Status') value.abilityModify(1.2, 'Venom Canticle');
			if (move.category !== 'Status' && !tidalBoosted && !value.abilityModify(1.2, 'Dissonant Echo') && !value.abilityModify(1.2, 'Liquid Voice')) value.abilityModify(1.2, 'Storm Calling');
		}
		if (!drillProfile && move.flags['slicing'] && !this.battle.hasPseudoWeather('Cold Eclipse Terrain')) {
			value.abilityModify(1.5, "Sharpness");
		}
		if ((move.flags['slicing'] || move.id === 'steelwing') && (!move.flags.slicing || !value.tryAbility('Sharpness')) &&
			!this.battle.hasPseudoWeather('Cold Eclipse Terrain')) {
			value.abilityModify(1.5, 'Imperial Edge');
		}
		for (let i = 1; i <= 5 && i <= pokemon.side.faintCounter; i++) {
			if (pokemon.volatiles[`fallen${i}`]) {
				value.abilityModify(1 + 0.1 * i, "Supreme Overlord");
			}
		}
		if (target) {
			if (move.category !== 'Status' && (!target.gender || target.gender === 'N' || pokemon.gender === target.gender)) {
                value.abilityModify(1.3, "Rivalry");
            }
		}
		const noTypeOverride = [
			'judgment', 'multiattack', 'naturalgift', 'revelationdance', 'struggle', 'technoblast', 'terrainpulse', 'weatherball',
		];
		const allowTypeOverride = !noTypeOverride.includes(move.id) && (move.id !== 'terablast' || !pokemon.terastallized);
		if (
			move.category !== 'Status' && allowTypeOverride && !move.isZ && !move.isMax &&
			!move.id.startsWith('hiddenpower')
		) {
			const plate = Dex.items.get(value.itemName);
			const typedMultipulse = move.id === 'multipulse' && plate.id.endsWith('plate') && plate.onPlate &&
				plate.onPlate !== 'Normal' && value.tryItem(plate.name);
			const gildedTypes = pokemon.getTypes(serverPokemon)[0];
			const typedGilded = ['gildedarrow', 'gildedhelix'].includes(move.id) &&
				(gildedTypes[1] && !['Fairy', 'Dark'].includes(gildedTypes[1]) ? gildedTypes[1] : gildedTypes[0]) !== 'Normal';
			if (move.type === 'Normal' && !typedMultipulse && !typedGilded) {
				value.abilityModify(this.battle.gen > 6 ? 1.2 : 1.3, "Aerilate");
				value.abilityModify(this.battle.gen > 6 ? 1.2 : 1.3, "Galvanize");
				value.abilityModify(this.battle.gen > 6 ? 1.2 : 1.3, "Pixilate");
				value.abilityModify(this.battle.gen > 6 ? 1.2 : 1.3, "Refrigerate");
				value.abilityModify(this.battle.hasPseudoWeather("Icy Terrain") ||
					this.battle.hasPseudoWeather("Snowy Mountain Terrain") ? 1.5 : 1.2, "Rimebreaker");
			}
			if (this.battle.gen > 6) {
				value.abilityModify(1.2, "Normalize");
			}
		}
		if (move.recoil || move.hasCrashDamage && !((move.flags as any).kick && value.tryAbility('Springheel'))) {
			value.abilityModify(1.2, 'Reckless');
		}
		if (moveType === 'Steel') {
			const steelMoveAbilityModifier = STEEL_MOVE_POWER_ABILITIES[value.abilityName];
			if (steelMoveAbilityModifier) value.abilityModify(steelMoveAbilityModifier, value.abilityName);
		}

		if (move.category !== 'Status') {
			let auraBoosted = '';
			let auraBroken = false;
			if (move.flags.sound) {
				for (const ally of this.battle.getAllActive()) {
					if (!ally || ally.fainted || !(ally.side === pokemon.side ||
						this.battle.gameType === 'multi' && ally.side.n % 2 === pokemon.side.n % 2)) continue;
					const own = ally === pokemon;
					const passives = own ? (serverPokemon.passives ?? ally.getSpecies().passives) : ally.getSpecies().passives;
					const selected = toID(ally.effectiveAbility(own ? serverPokemon : undefined));
					const resonance = Dex.getAbilityEffects(selected).has('resonanceforce' as ID) ||
						passives.some(passive => Dex.getPassiveAbilityEffects(toID(passive)).has('resonanceforce' as ID));
					if (resonance) value.modify(1.5, 'Resonance Force');
				}
			}
			for (const ally of pokemon.side.active) {
				if (!ally || ally.fainted) continue;
				let allyAbility = this.getAllyAbility(ally);
				if (moveType === 'Fairy' && allyAbility === 'Fairy Aura') {
					auraBoosted = 'Fairy Aura';
				} else if (moveType === 'Dark' && allyAbility === 'Dark Aura') {
					auraBoosted = 'Dark Aura';
				} else if (allyAbility === 'Aura Break') {
					auraBroken = true;
				} else if (allyAbility === 'Battery' && ally !== pokemon && move.category === 'Special') {
					value.modify(1.3, 'Battery');
				} else if (allyAbility === 'Power Spot' && ally !== pokemon) {
					value.modify(1.3, 'Power Spot');
				} else if (allyAbility === 'Steely Spirit' && moveType === 'Steel') {
					value.modify(1.5, allyAbility);
				}
			}
			for (const foe of pokemon.side.foe.active) {
				if (!foe || foe.fainted) continue;
				if (foe.ability === 'Fairy Aura' && moveType === 'Fairy') {
					auraBoosted = 'Fairy Aura';
				} else if (foe.ability === 'Dark Aura' && moveType === 'Dark') {
					auraBoosted = 'Dark Aura';
				} else if (foe.ability === 'Aura Break') {
					auraBroken = true;
				}
			}
			if (auraBoosted) {
				if (auraBroken) {
					value.modify(0.75, auraBoosted + ' + Aura Break');
				} else {
					value.modify(1.33, auraBoosted);
				}
			}
		}

		const passives = serverPokemon.passives ?? pokemon.getSpecies(serverPokemon).passives;
		if (move.category !== 'Status' && moveType === 'Fire' && value.tryAbility('Fire Mane')) {
			value.powerAbilityModify(1.5, 'Fire Mane');
		}

		if (move.category !== 'Status' && passives.includes('proficient') && pokemon.getTypeList(serverPokemon).includes(moveType)) {
			value.powerAbilityModify(1.3, 'Proficient passive');
		}

		// Terrain
		if (!fieldPower) {
			if ((this.battle.hasPseudoWeather('Electric Terrain') && moveType === 'Electric') ||
				(this.battle.hasPseudoWeather('Grassy Terrain') && moveType === 'Grass') ||
				(this.battle.hasPseudoWeather('Psychic Terrain') && moveType === 'Psychic')) {
				if (pokemon.isGrounded(serverPokemon)) {
					value.modify(this.battle.gen > 7 ? 1.3 : 1.5, 'Terrain boost');
				}
			} else if (this.battle.hasPseudoWeather('Misty Terrain') && moveType === 'Dragon') {
				if (target ? target.isGrounded() : true) {
					value.modify(0.5, 'Misty Terrain + grounded target');
				}
			} else if (
				this.battle.hasPseudoWeather('Grassy Terrain') && ['earthquake', 'bulldoze', 'magnitude'].includes(move.id)
			) {
				if (target ? target.isGrounded() : true) {
					value.modify(0.5, 'Grassy Terrain + grounded target');
				}
			}
			if (
				move.id === 'expandingforce' &&
				this.battle.hasPseudoWeather('Psychic Terrain') &&
				pokemon.isGrounded(serverPokemon)
			) {
				value.modify(1.5, 'Expanding Force + Psychic Terrain boost');
			}
			if (move.id === 'mistyexplosion' && this.battle.hasPseudoWeather('Misty Terrain')) {
				value.modify(1.5, 'Misty Explosion + Misty Terrain boost');
			}
			if (move.id === 'risingvoltage' && this.battle.hasPseudoWeather('Electric Terrain') && target?.isGrounded()) {
				value.modify(2, 'Rising Voltage + Electric Terrain boost');
			}
		}

	 if (value.dualWieldPower) {
		// These conditions depend on queued actions or private entry budgets, which public tooltips cannot infer.
		for (const [ability, factor] of [['Analytic', 1.3], ['Last Laugh', 1.3], ['Stakeout', 2]] as const) {
		if (value.tryAbility(ability)) {value.dualWieldPotentialPower *= factor; value.comment.push(' (' + ability + ': conditional activation)'); }
		}
		if (target && ((moveType === 'Fire' && ['psn', 'tox'].includes(target.status) &&
			(value.tryAbility('Corrosive Burn') || value.tryAbility('Venom Ignition'))) ||
		(moveType === 'Ice' && target.status === 'brn' && value.tryAbility('Freezer Burn')) ||
		(moveType === 'Steel' && move.category === 'Physical' && (target.boosts.def || 0) < 0 && value.tryAbility('Crushing Depths')) ||
		(value.tryAbility('Rival Malice') && target.hp > 0))) value.dualWieldBoosted = true;
		if ((this.battle.hasPseudoWeather('Fairy Tale Terrain') || this.battle.hasPseudoWeather('Chessboard Terrain')) &&
		(value.tryAbility('Queenly Majesty') || value.tryAbility('Empress') || value.tryAbility('Void Voice'))) {
			value.dualWieldPotentialDamage = true;
		}
		for (const ability of ['Sniper', 'Abyss Sniper', 'Patient Marksman', 'Neuroforce', 'Tinted Lens', 'Desert Spirit', 'Prism Wings', 'Fresh Plumage', 'Ruined Sky', 'Eternal Flower', 'RKS System']) {
		if (!value.tryAbility(ability)) continue;
		if (['Sniper', 'Abyss Sniper', 'Patient Marksman'].includes(ability) && (move as any).willCrit) {
			value.dualWieldBoosted = true;
		} else {
			value.dualWieldPotentialDamage = true;
		}
		}
	}
		// Item
		value = this.getItemBoost(move, value, moveType);

		// Terastal base power floor
		if (
			pokemon.terastallized && (pokemon.terastallized === move.type || pokemon.terastallized === 'Stellar') &&
			value.value < 60 && move.priority <= 0 && !move.multihit && !(
				(move.basePower === 0 || move.basePower === 150) && move.basePowerCallback
			)
		) {
			value.set(60, 'Tera type BP minimum');
		}

		if (fieldPower && move.category !== 'Status') {
			const reason = fieldPower.field.name;
			if (fieldPower.min !== fieldPower.max) {
				if (fieldPower.field.id === 'watersurfaceterrain' && move.id === 'sludgewave') {
					value.setRange(Math.floor(value.value * fieldPower.min),
						Math.floor((value.maxValue || value.value) * fieldPower.max),
						reason + '; first / second Sludge Wave');
					value.rangeSeparator = ' or ';
				} else {
					value.setRange(value.value * fieldPower.min, (value.maxValue || value.value) * fieldPower.max,
						reason + '; varies with field conditions');
				}
			} else if (fieldPower.min === 0) {
				value.set(0, 'Fails in ' + reason);
			} else if (fieldPower.min !== 1) {
				value.modify(fieldPower.min, reason);
			}
		}

		if (!move.isZ && !move.isMax && this.battle.hasPseudoWeather('Gravity')) {
			if (move.flags.gravityboost) value.modify(5325 / 4096, 'Gravity');
			if (move.flags.wind) value.modify(0.75, 'Gravity: weakened wind');
		}
		if (this.battle.hasPseudoWeather('Magic Room') && ['Psychic', 'Ghost', 'Fairy'].includes(moveType)) {
			value.modify(4915 / 4096, 'Magic Room');
		}
		if (this.battle.hasPseudoWeather('Wonder Room') && !move.isZ && !move.isMax &&
			move.overrideOffensiveStat && ['def', 'spd'].includes(move.overrideOffensiveStat)) {
			value.modify(4915 / 4096, 'Wonder Room');
		}
		if (pokemon.volatiles['sirensveil'] && move.category !== 'Status') value.modify(1.3, 'Siren’s Veil ready');
		if (pokemon.volatiles.worldaxis && move.category !== 'Status' && value.tryAbility('World Axis')) {
			value.modify(1.5, 'World Axis charge');
		}
		if (move.category !== 'Status' && ['Fire', 'Electric'].includes(moveType) && value.tryAbility('Plasma Eruption')) {
			if (pokemon.volatiles.plasmapressure3) value.modify(1.6, 'Plasma Eruption: core ready');
			if (pokemon.volatiles.plasmaairborne && moveType === 'Electric') value.modify(1.2, 'Plasma Airborne');
			if (pokemon.volatiles.plasmalanding && moveType === 'Fire') value.modify(1.3, 'Plasma Landing');
			const fieldIs = (ids: string[]) => ids.some(id => this.battle.hasPseudoWeather(id));
			let fieldBonus = 1;
			if (moveType === 'Fire' && fieldIs(['Volcanic Terrain', 'Burning Terrain', 'Superheated Terrain', "Dragon's Den Terrain"])) fieldBonus = 1.15;
			else if (moveType === 'Electric' && fieldIs(['Electric Terrain', 'Electric Aura', 'Factory Terrain', 'Short-Circuit Terrain'])) fieldBonus = 1.15;
			else if (fieldIs(['Mountain Terrain', 'Cave Terrain', 'Crystal Cavern Terrain', 'Desert Terrain', 'Ashen Beach Terrain']) ||
				['sunnyday', 'desolateland'].includes(value.effectiveWeather())) fieldBonus = 1.1;
			else if (fieldIs(['Haunted Terrain', 'New World Terrain', 'Inverse Terrain', 'Big Top Terrain', 'Fairy Tale Terrain'])) fieldBonus = 1.05;
			if (fieldBonus !== 1) value.modify(fieldBonus, 'Plasma Eruption: field bonus');
		}

		// Burn isn't really a base power modifier, so it needs to be applied after the Tera BP floor
		if (this.battle.gen > 2 && serverPokemon.status === 'brn' && move.id !== 'facade' && move.category === 'Physical') {
			if (!value.tryAbility("Guts")) value.modify(0.5, 'Burn');
		}

		if (
			move.id === 'steelroller' &&
			!this.battle.hasPseudoWeather('Electric Terrain') &&
			!this.battle.hasPseudoWeather('Grassy Terrain') &&
			!this.battle.hasPseudoWeather('Misty Terrain') &&
			!this.battle.hasPseudoWeather('Psychic Terrain')
		) {
			value.set(0, 'no Terrain');
		}

	 if (value.dualWieldPower && !value.dualWieldFull) {
		const conditional = value.dualWieldPotentialPower > 1 || value.dualWieldPotentialDamage;
		if (conditional) {
		const low = value.value;
		const high = value.maxValue || low;
		if (hit === 1) value.setRange(low * (value.dualWieldBoosted ? 1 : 0.6), high * value.dualWieldPotentialPower, 'conditional offensive ability; damage multipliers apply separately');
		else value.setRange(low * 0.15, high * (value.dualWieldBoosted ? 0.15 : 0.6), '15% when an offensive ability activates; otherwise 60%');
		} else value.modify(value.dualWieldBoosted ? hit > 1 ? 0.15 : 1 : 0.6, 'Dual Wield');
	}
  if (hit === 1 && drillProfile?.hits.length === 2) {
   const second = this.getMoveBasePower(move, moveType, new ModifiableValue(this.battle, pokemon, serverPokemon), target, 2);
   value.secondHitPower = second.value;
		 if (second.dualWieldPotentialPower > 1 || second.dualWieldPotentialDamage) value.comment.push(' (follow-up is 15% when the offensive ability activates; otherwise 60%)');
   value.comment.push(' (first hit; second hit: ' + second.round(second.value) + (second.maxValue ? ' to ' + second.round(second.maxValue) : '') + ' BP; each hit checks accuracy)');
  }
		return value;
	}

	static incenseTypes: {[itemName: string]: TypeName} = {
		'Odd Incense': 'Psychic',
		'Rock Incense': 'Rock',
		'Rose Incense': 'Grass',
		'Sea Incense': 'Water',
		'Wave Incense': 'Water',
	};
	static itemTypes: {[itemName: string]: TypeName} = {
		'Black Belt': 'Fighting',
		'Black Glasses': 'Dark',
		'Charcoal': 'Fire',
		'Dragon Fang': 'Dragon',
		'Fairy Feather': 'Fairy',
		'Hard Stone': 'Rock',
		'Magnet': 'Electric',
		'Metal Coat': 'Steel',
		'Miracle Seed': 'Grass',
		'Mystic Water': 'Water',
		'Never-Melt Ice': 'Ice',
		'Poison Barb': 'Poison',
		'Sharp Beak': 'Flying',
		'Silk Scarf': 'Normal',
		'Silver Powder': 'Bug',
		'Soft Sand': 'Ground',
		'Spell Tag': 'Ghost',
		'Twisted Spoon': 'Psychic',
	};
	static orbUsers: {[speciesForme: string]: string[]} = {
		'Latias': ['Soul Dew'],
		'Latios': ['Soul Dew'],
		'Dialga': ['Adamant Crystal', 'Adamant Orb'],
		'Palkia': ['Lustrous Globe', 'Lustrous Orb'],
		'Giratina': ['Griseous Core', 'Griseous Orb'],
		'Venomicon': ['Vile Vial'],
	};
	static orbTypes: {[itemName: string]: TypeName[]} = {
		'Soul Dew': ['Psychic', 'Dragon'],
		'Adamant Crystal': ['Steel', 'Dragon'],
		'Adamant Orb': ['Steel', 'Dragon'],
		'Lustrous Globe': ['Water', 'Dragon'],
		'Lustrous Orb': ['Water', 'Dragon'],
		'Griseous Core': ['Ghost', 'Dragon'],
		'Griseous Orb': ['Ghost', 'Dragon'],
		'Vile Vial': ['Poison', 'Flying'],
	};
	static noGemMoves = [
		'Fire Pledge',
		'Fling',
		'Grass Pledge',
		'Struggle',
		'Water Pledge',
	];
	getItemBoost(move: Move, value: ModifiableValue, moveType: TypeName) {
		let item = this.battle.dex.items.get(value.serverPokemon.item);
		let itemName = item.name;
		let moveName = move.name;
		let species = this.battle.dex.species.get(value.serverPokemon.speciesForme);
		let isTransform = value.pokemon.volatiles.transform;
		let speciesName = isTransform && value.pokemon.volatiles.formechange?.[1] && this.battle.gen <= 4 ?
			this.battle.dex.species.get(value.pokemon.volatiles.formechange[1]).baseSpecies : species.baseSpecies;

		// Plates
		if (item.onPlate === moveType && !item.zMove) {
			value.itemModify(1.2);
			return value;
		}

		// Incenses
		if (BattleTooltips.incenseTypes[item.name] === moveType) {
			value.itemModify(1.2);
			return value;
		}

		// Type-enhancing items
		if (BattleTooltips.itemTypes[item.name] === moveType) {
			value.itemModify(this.battle.gen < 4 ? 1.1 : 1.2);
			return value;
		}

		// Light ball is a base power modifier in gen 4 only
		if (item.name === 'Light Ball' && this.battle.gen === 4 && speciesName === 'Pikachu') {
			value.itemModify(2);
			return value;
		}

		// Pokemon-specific items
		if (item.name === 'Soul Dew' && this.battle.gen < 7) return value;
		if (BattleTooltips.orbUsers[speciesName]?.includes(item.name) &&
			BattleTooltips.orbTypes[item.name]?.includes(moveType)) {
			value.itemModify(1.2);
			return value;
		}
		if ((speciesName.startsWith('Ogerpon-Wellspring') && itemName === 'Wellspring Mask') ||
			(speciesName.startsWith('Ogerpon-Hearthflame') && itemName === 'Hearthflame Mask') ||
			(speciesName.startsWith('Ogerpon-Cornerstone') && itemName === 'Cornerstone Mask')) {
			value.itemModify(1.2);
			return value;
		}

		// Gems
		if (BattleTooltips.noGemMoves.includes(moveName)) return value;
		if (itemName === moveType + ' Gem') {
			value.itemModify(this.battle.gen < 6 ? 1.5 : 1.3);
			return value;
		}

		if (itemName === 'Muscle Band' && move.category === 'Physical' ||
			itemName === 'Wise Glasses' && move.category === 'Special' ||
			itemName === 'Punching Glove' && move.flags['punch']) {
			value.itemModify(1.1);
		}

		return value;
	}
	getPokemonTypes(pokemon: Pokemon | ServerPokemon, preterastallized = false): ReadonlyArray<TypeName> {
		if (!(pokemon as Pokemon).getTypes) {
			return this.battle.dex.species.get(pokemon.speciesForme).types;
		}

		return (pokemon as Pokemon).getTypeList(undefined, preterastallized);
	}
	pokemonHasType(pokemon: Pokemon | ServerPokemon, type: TypeName, types?: ReadonlyArray<TypeName>) {
		if (!types) types = this.getPokemonTypes(pokemon);
		for (const curType of types) {
			if (curType === type) return true;
		}
		return false;
	}
	getAllyAbility(ally: Pokemon) {
		// this will only be available if the ability announced itself in some way
		let allyAbility = Dex.abilities.get(ally.ability).name;
		// otherwise fall back on the original set data sent from the server
		if (!allyAbility) {
			if (this.battle.myAllyPokemon) { // multi battle ally
				allyAbility = Dex.abilities.get(this.battle.myAllyPokemon[ally.slot].ability).name;
			} else if (this.battle.myPokemon) {
				allyAbility = Dex.abilities.get(this.battle.myPokemon[ally.slot].ability).name;
			}
		}
		return allyAbility;
	}
	getPokemonAbilityData(clientPokemon: Pokemon | null, serverPokemon: ServerPokemon | null | undefined) {
		const abilityData: {ability: string, baseAbility: string, possibilities: string[]} = {
			ability: '', baseAbility: '', possibilities: [],
		};
		if (clientPokemon) {
			if (clientPokemon.ability) {
				abilityData.ability = clientPokemon.ability || clientPokemon.baseAbility;
				if (clientPokemon.baseAbility) {
					abilityData.baseAbility = clientPokemon.baseAbility;
				}
			} else {
				const speciesForme = clientPokemon.getSpeciesForme() || serverPokemon?.speciesForme || '';
				const species = this.battle.dex.species.get(speciesForme);
				if (species.exists && species.abilities) {
					abilityData.possibilities = [species.abilities['0']];
					if (species.abilities['1']) abilityData.possibilities.push(species.abilities['1']);
					if (species.abilities['H']) abilityData.possibilities.push(species.abilities['H']);
					if (species.abilities['S']) abilityData.possibilities.push(species.abilities['S']);
					if (this.battle.rules['Frantic Fusions Mod']) {
						const fusionSpecies = this.battle.dex.species.get(clientPokemon.name);
						if (fusionSpecies.exists && fusionSpecies.name !== species.name) {
							abilityData.possibilities = Array.from(
								new Set(abilityData.possibilities.concat(Object.values(fusionSpecies.abilities)))
							);
						}
					}
				}
			}
		}
		if (serverPokemon) {
			if (!abilityData.ability) abilityData.ability = serverPokemon.ability || serverPokemon.baseAbility;
			if (!abilityData.baseAbility && serverPokemon.baseAbility) {
				abilityData.baseAbility = serverPokemon.baseAbility;
			}
		}
		return abilityData;
	}
	getPokemonAbilityText(
		clientPokemon: Pokemon | null,
		serverPokemon: ServerPokemon | null | undefined,
		isActive: boolean | undefined,
		hidePossible?: boolean
	) {
		let text = '';
		const abilityData = this.getPokemonAbilityData(clientPokemon, serverPokemon);
		if (!isActive) {
			// for switch tooltips, only show the original ability
			const ability = abilityData.baseAbility || abilityData.ability;
			if (ability) text = '<small>Ability:</small> ' + Dex.abilities.get(ability).name;
		} else {
			if (abilityData.ability) {
				const abilityName = Dex.abilities.get(abilityData.ability).name;
				text = '<small>Ability:</small> ' + abilityName;
				const baseAbilityName = Dex.abilities.get(abilityData.baseAbility).name;
				if (baseAbilityName && baseAbilityName !== abilityName) text += ' (base: ' + baseAbilityName + ')';
			}
		}
		const knownAbility = isActive ? abilityData.ability : (abilityData.baseAbility || abilityData.ability);
		if (knownAbility) {
			const passives = serverPokemon?.passives ?? clientPokemon?.getSpecies().passives ?? [];
			const species = clientPokemon?.getSpecies().id || serverPokemon?.speciesForme || '';
			const item = serverPokemon ? serverPokemon.item : clientPokemon?.item || (clientPokemon?.prevItem ? '' : undefined);
			const shown = Dex.getAbilityDisplayDetails(this.battle.dex.abilities.get(knownAbility), passives, species, item);
			const parts = Dex.getAbilityDisplayComponents(shown.id, this.battle.dex, passives, species, item);
			const forecastSuppressed = shown.id === 'perfectforecast' && (isActive || clientPokemon?.isActive()) && clientPokemon && !clientPokemon.effectiveAbility(serverPokemon || undefined);
			text += '<br /><small>' + BattleLog.escapeHTML(forecastSuppressed ? 'Perfect Forecast suppressed: no form bonuses. Passive Forecast still changes form.' : shown.shortDesc) + '</small>';
			if (parts.length) text += '<br /><small>Includes: ' + parts.map(id => BattleLog.escapeHTML(Dex.abilities.get(id).name)).join(' · ') + '</small>';
		}
		const tier = this.battle.tier;
		if (!text && abilityData.possibilities.length && !hidePossible &&
			!(tier.includes('Almost Any Ability') || tier.includes('Hackmons') ||
				tier.includes('Inheritance') || tier.includes('Metronome'))) {
			text = '<small>Possible abilities:</small> ' + abilityData.possibilities.join(', ');
		}
		return text;
	}
}

type StatsTable = {hp: number, atk: number, def: number, spa: number, spd: number, spe: number};

/**
 * PokemonSet can be sparse, in which case that entry should be
 * inferred from the rest of the set, according to sensible
 * defaults.
 */
interface PokemonSet {
	initialDisguise?: number;
	/** Defaults to species name (not including forme), like in games */
	name?: string;
	species: string;
	/** Defaults to no item */
	item?: string;
	/** Defaults to no ability (error in Gen 3+) */
	ability?: string;
	moves: string[];
	/** Defaults to no nature (error in Gen 3+) */
	nature?: NatureName;
	/** Defaults to random legal gender, NOT subject to gender ratios */
	gender?: string;
	/** Defaults to flat 252's (200's/0's in Let's Go) (error in gen 3+) */
	evs?: StatsTable;
	/** Defaults to whatever makes sense - flat 31's unless you have Gyro Ball etc */
	ivs?: StatsTable;
	/** Defaults as you'd expect (100 normally, 50 in VGC-likes, 5 in LC) */
	level?: number;
	/** Defaults to no (error if shiny event) */
	shiny?: boolean;
	/** Defaults to 255 unless you have Frustration, in which case 0 */
	happiness?: number;
	/** Defaults to event required ball, otherwise Poké Ball */
	pokeball?: string;
	/** Defaults to the type of your Hidden Power in Moves, otherwise Dark */
	hpType?: string;
	/** Defaults to 10 */
	dynamaxLevel?: number;
	/** Defaults to no (can only be yes for certain Pokemon) */
	gigantamax?: boolean;
	/** Defaults to the primary type */
	teraType?: string;
}

class BattleStatGuesser {
	formatid: ID;
	dex: ModdedDex;
	moveCount: any = null;
	hasMove: any = null;

	ignoreEVLimits: boolean;
	supportsEVs: boolean;
	supportsAVs: boolean;

	constructor(formatid: ID) {
		this.formatid = formatid;
		this.dex = formatid ? Dex.mod(formatid.slice(0, 4) as ID) : Dex;
		this.ignoreEVLimits = (
			this.dex.gen < 3 ||
			((this.formatid.endsWith('hackmons') || this.formatid.endsWith('bh')) && this.dex.gen !== 6) ||
			this.formatid.includes('metronomebattle') ||
			this.formatid.endsWith('norestrictions')
		);
		this.supportsEVs = !this.formatid.includes('letsgo');
		this.supportsAVs = !this.supportsEVs && this.formatid.endsWith('norestrictions');
	}
	guess(set: PokemonSet) {
		let role = this.guessRole(set);
		let comboEVs = this.guessEVs(set, role);
		let evs = {hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0};
		for (let stat in evs) {
			evs[stat as StatName] = comboEVs[stat as StatName] || 0;
		}
		let plusStat: StatNameExceptHP | '' = comboEVs.plusStat || '';
		let minusStat: StatNameExceptHP | '' = comboEVs.minusStat || '';
		return {role, evs, plusStat, minusStat, moveCount: this.moveCount, hasMove: this.hasMove};
	}
	guessRole(set: PokemonSet) {
		if (!set) return '?';
		if (!set.moves) return '?';

		let moveCount = {
			'Physical': 0,
			'Special': 0,
			'PhysicalAttack': 0,
			'SpecialAttack': 0,
			'PhysicalSetup': 0,
			'SpecialSetup': 0,
			'Support': 0,
			'Setup': 0,
			'Restoration': 0,
			'Offense': 0,
			'Stall': 0,
			'SpecialStall': 0,
			'PhysicalStall': 0,
			'Fast': 0,
			'Ultrafast': 0,
			'bulk': 0,
			'specialBulk': 0,
			'physicalBulk': 0,
		};
		let hasMove: {[moveid: string]: 1} = {};
		let itemid = toID(set.item);
		let item = this.dex.items.get(itemid);
		let abilityid = toID(set.ability);

		let species = this.dex.species.get(set.species || set.name!);
		if (item.megaEvolves === species.name) species = this.dex.species.get(item.megaStone);
		if (!species.exists) return '?';
		let stats = species.baseStats;

		if (set.moves.length < 1) return '?';
		let needsFourMoves = !['unown', 'ditto'].includes(species.id);
		let moveids = set.moves.map(toID);
		if (moveids.includes('lastresort' as ID)) needsFourMoves = false;
		if (set.moves.length < 4 && needsFourMoves && !this.formatid.includes('metronomebattle')) {
			return '?';
		}

		for (let i = 0, len = set.moves.length; i < len; i++) {
			let move = this.dex.moves.get(set.moves[i]);
			hasMove[move.id] = 1;
			if (move.category === 'Status') {
				if (['batonpass', 'healingwish', 'lunardance'].includes(move.id)) {
					moveCount['Support']++;
				} else if (['metronome', 'assist', 'copycat', 'mefirst', 'photongeyser', 'shellsidearm'].includes(move.id)) {
					moveCount['Physical'] += 0.5;
					moveCount['Special'] += 0.5;
				} else if (move.id === 'naturepower') {
					moveCount['Special']++;
				} else if (['protect', 'detect', 'spikyshield', 'kingsshield'].includes(move.id)) {
					moveCount['Stall']++;
				} else if (move.id === 'wish') {
					moveCount['Restoration']++;
					moveCount['Stall']++;
					moveCount['Support']++;
				} else if (move.heal) {
					moveCount['Restoration']++;
					moveCount['Stall']++;
				} else if (move.target === 'self') {
					if (['agility', 'rockpolish', 'shellsmash', 'growth', 'workup'].includes(move.id)) {
						moveCount['PhysicalSetup']++;
						moveCount['SpecialSetup']++;
					} else if (['dragondance', 'swordsdance', 'coil', 'bulkup', 'curse', 'bellydrum'].includes(move.id)) {
						moveCount['PhysicalSetup']++;
					} else if (['nastyplot', 'tailglow', 'quiverdance', 'calmmind', 'geomancy'].includes(move.id)) {
						moveCount['SpecialSetup']++;
					}
					if (move.id === 'substitute') moveCount['Stall']++;
					moveCount['Setup']++;
				} else {
					if (['toxic', 'leechseed', 'willowisp', 'coldsnap'].includes(move.id)) {
						moveCount['Stall']++;
					}
					moveCount['Support']++;
				}
			} else if (['counter', 'endeavor', 'metalburst', 'mirrorcoat', 'rapidspin'].includes(move.id)) {
				moveCount['Support']++;
			} else if ([
				'nightshade', 'seismictoss', 'psywave', 'superfang', 'naturesmadness', 'foulplay', 'endeavor', 'finalgambit', 'bodypress',
			].includes(move.id)) {
				moveCount['Offense']++;
			} else if (move.id === 'fellstinger') {
				moveCount['PhysicalSetup']++;
				moveCount['Setup']++;
			} else {
				moveCount[move.category]++;
				moveCount['Offense']++;
				if (move.id === 'knockoff') {
					moveCount['Support']++;
				}
				if (['scald', 'voltswitch', 'uturn', 'flipturn'].includes(move.id)) {
					moveCount[move.category] -= 0.2;
				}
			}
		}
		if (hasMove['batonpass']) moveCount['Support'] += moveCount['Setup'];
		moveCount['PhysicalAttack'] = moveCount['Physical'];
		moveCount['Physical'] += moveCount['PhysicalSetup'];
		moveCount['SpecialAttack'] = moveCount['Special'];
		moveCount['Special'] += moveCount['SpecialSetup'];

		if (hasMove['dragondance'] || hasMove['quiverdance']) moveCount['Ultrafast'] = 1;

		let isFast = (stats.spe >= 80);
		let physicalBulk = (stats.hp + 75) * (stats.def + 87);
		let specialBulk = (stats.hp + 75) * (stats.spd + 87);

		if (hasMove['willowisp'] || hasMove['acidarmor'] || hasMove['irondefense'] || hasMove['cottonguard']) {
			physicalBulk *= 1.6;
			moveCount['PhysicalStall']++;
		} else if (hasMove['scald'] || hasMove['bulkup'] || hasMove['coil'] || hasMove['cosmicpower']) {
			physicalBulk *= 1.3;
			if (hasMove['scald']) { // partial stall goes in reverse
				moveCount['SpecialStall']++;
			} else {
				moveCount['PhysicalStall']++;
			}
		}
		if (abilityid === 'flamebody') physicalBulk *= 1.1;

		if (hasMove['calmmind'] || hasMove['quiverdance'] || hasMove['geomancy']) {
			specialBulk *= 1.3;
			moveCount['SpecialStall']++;
		}
		if (abilityid === 'sandstream' && species.types.includes('Rock')) {
			specialBulk *= 1.5;
		}

		if (hasMove['bellydrum']) {
			physicalBulk *= 0.6;
			specialBulk *= 0.6;
		}
		if (moveCount['Restoration']) {
			physicalBulk *= 1.5;
			specialBulk *= 1.5;
		} else if (hasMove['painsplit'] && hasMove['substitute']) {
			// SubSplit isn't generally a stall set
			moveCount['Stall']--;
		} else if (hasMove['painsplit'] || hasMove['rest']) {
			physicalBulk *= 1.4;
			specialBulk *= 1.4;
		}
		if ((hasMove['bodyslam'] || hasMove['thunder']) && abilityid === 'serenegrace' || hasMove['thunderwave']) {
			physicalBulk *= 1.1;
			specialBulk *= 1.1;
		}
		if ((hasMove['ironhead'] || hasMove['airslash']) && abilityid === 'serenegrace') {
			physicalBulk *= 1.1;
			specialBulk *= 1.1;
		}
		if (hasMove['gigadrain'] || hasMove['drainpunch'] || hasMove['hornleech']) {
			physicalBulk *= 1.15;
			specialBulk *= 1.15;
		}
		if (itemid === 'leftovers' || itemid === 'blacksludge') {
			physicalBulk *= 1 + 0.1 * (1 + moveCount['Stall'] / 1.5);
			specialBulk *= 1 + 0.1 * (1 + moveCount['Stall'] / 1.5);
		}
		if (hasMove['leechseed']) {
			physicalBulk *= 1 + 0.1 * (1 + moveCount['Stall'] / 1.5);
			specialBulk *= 1 + 0.1 * (1 + moveCount['Stall'] / 1.5);
		}
		if ((itemid === 'flameorb' || itemid === 'toxicorb') && abilityid !== 'magicguard') {
			if (itemid === 'toxicorb' && abilityid === 'poisonheal') {
				physicalBulk *= 1 + 0.1 * (2 + moveCount['Stall']);
				specialBulk *= 1 + 0.1 * (2 + moveCount['Stall']);
			} else {
				physicalBulk *= 0.8;
				specialBulk *= 0.8;
			}
		}
		if (itemid === 'lifeorb') {
			physicalBulk *= 0.7;
			specialBulk *= 0.7;
		}
		if (abilityid === 'multiscale' || abilityid === 'magicguard' || abilityid === 'regenerator') {
			physicalBulk *= 1.4;
			specialBulk *= 1.4;
		}
		if (itemid === 'eviolite' || (itemid === 'eeviumz' && ['eevee', 'eeveestarter', 'eeveestarteralt', 'eeveegmax'].includes(species.id))) {
			physicalBulk *= 1.5;
			specialBulk *= 1.5;
		}
		if (itemid === 'assaultvest') {
			specialBulk *= 1.5;
		}

		let bulk = physicalBulk + specialBulk;
		if (bulk < 46000 && stats.spe >= 70) isFast = true;
		if (hasMove['trickroom']) isFast = false;
		moveCount['bulk'] = bulk;
		moveCount['physicalBulk'] = physicalBulk;
		moveCount['specialBulk'] = specialBulk;

		if (
			hasMove['agility'] || hasMove['dragondance'] || hasMove['quiverdance'] ||
			hasMove['rockpolish'] || hasMove['shellsmash'] || hasMove['flamecharge']
		) {
			isFast = true;
		} else if (abilityid === 'unburden' || abilityid === 'speedboost' || abilityid === 'motordrive') {
			isFast = true;
			moveCount['Ultrafast'] = 1;
		} else if (abilityid === 'chlorophyll' || abilityid === 'swiftswim' || abilityid === 'sandrush') {
			isFast = true;
			moveCount['Ultrafast'] = 2;
		} else if (itemid === 'salacberry') {
			isFast = true;
		}
		const ultrafast = hasMove['agility'] || hasMove['shellsmash'] ||
			hasMove['autotomize'] || hasMove['shiftgear'] || hasMove['rockpolish'];
		if (ultrafast) {
			moveCount['Ultrafast'] = 2;
		}
		moveCount['Fast'] = isFast ? 1 : 0;

		this.moveCount = moveCount;
		this.hasMove = hasMove;

		if (species.id === 'ditto') return abilityid === 'imposter' ? 'Physically Defensive' : 'Fast Bulky Support';
		if (species.id === 'shedinja') return 'Fast Physical Sweeper';

		if (itemid === 'choiceband' && moveCount['PhysicalAttack'] >= 2) {
			if (!isFast) return 'Bulky Band';
			return 'Fast Band';
		} else if (itemid === 'choicespecs' && moveCount['SpecialAttack'] >= 2) {
			if (!isFast) return 'Bulky Specs';
			return 'Fast Specs';
		} else if (itemid === 'choicescarf') {
			if (moveCount['PhysicalAttack'] === 0) return 'Special Scarf';
			if (moveCount['SpecialAttack'] === 0) return 'Physical Scarf';
			if (moveCount['PhysicalAttack'] > moveCount['SpecialAttack']) return 'Physical Biased Mixed Scarf';
			if (moveCount['PhysicalAttack'] < moveCount['SpecialAttack']) return 'Special Biased Mixed Scarf';
			if (stats.atk < stats.spa) return 'Special Biased Mixed Scarf';
			return 'Physical Biased Mixed Scarf';
		}

		if (species.id === 'unown') return 'Fast Special Sweeper';

		if (moveCount['PhysicalStall'] && moveCount['Restoration']) {
			if (stats.spe > 110 && abilityid !== 'prankster') return 'Fast Bulky Support';
			return 'Specially Defensive';
		}
		if (moveCount['SpecialStall'] && moveCount['Restoration'] && itemid !== 'lifeorb') {
			if (stats.spe > 110 && abilityid !== 'prankster') return 'Fast Bulky Support';
			return 'Physically Defensive';
		}

		let offenseBias: 'Physical' | 'Special' = 'Physical';
		if (stats.spa > stats.atk && moveCount['Special'] > 1) offenseBias = 'Special';
		else if (stats.atk > stats.spa && moveCount['Physical'] > 1) offenseBias = 'Physical';
		else if (moveCount['Special'] > moveCount['Physical']) offenseBias = 'Special';

		if (moveCount['Stall'] + moveCount['Support'] / 2 <= 2 && bulk < 135000 && moveCount[offenseBias] >= 1.5) {
			if (isFast) {
				if (bulk > 80000 && !moveCount['Ultrafast']) return 'Bulky ' + offenseBias + ' Sweeper';
				return 'Fast ' + offenseBias + ' Sweeper';
			} else {
				if (moveCount[offenseBias] >= 3 || moveCount['Stall'] <= 0) {
					return 'Bulky ' + offenseBias + ' Sweeper';
				}
			}
		}

		if (isFast && abilityid !== 'prankster') {
			if (stats.spe > 100 || bulk < 55000 || moveCount['Ultrafast']) {
				return 'Fast Bulky Support';
			}
		}
		if (moveCount['SpecialStall']) return 'Physically Defensive';
		if (moveCount['PhysicalStall']) return 'Specially Defensive';
		if (species.id === 'blissey' || species.id === 'chansey') return 'Physically Defensive';
		if (specialBulk >= physicalBulk) return 'Specially Defensive';
		return 'Physically Defensive';
	}
	ensureMinEVs(evs: StatsTable, stat: StatName, min: number, evTotal: number) {
		if (!evs[stat]) evs[stat] = 0;
		let diff = min - evs[stat];
		if (diff <= 0) return evTotal;
		if (evTotal <= 504) {
			let change = Math.min(508 - evTotal, diff);
			evTotal += change;
			evs[stat] += change;
			diff -= change;
		}
		if (diff <= 0) return evTotal;
		let evPriority = {def: 1, spd: 1, hp: 1, atk: 1, spa: 1, spe: 1};
		let prioStat: StatName;
		for (prioStat in evPriority) {
			if (prioStat === stat) continue;
			if (evs[prioStat] && evs[prioStat] > 128) {
				evs[prioStat] -= diff;
				evs[stat] += diff;
				return evTotal;
			}
		}
		return evTotal; // can't do it :(
	}
	ensureMaxEVs(evs: StatsTable, stat: StatName, min: number, evTotal: number) {
		if (!evs[stat]) evs[stat] = 0;
		let diff = evs[stat] - min;
		if (diff <= 0) return evTotal;
		evs[stat] -= diff;
		evTotal -= diff;
		return evTotal; // can't do it :(
	}
	guessEVs(set: PokemonSet, role: string): Partial<StatsTable> & {plusStat?: StatNameExceptHP | '', minusStat?: StatNameExceptHP | ''} {
		if (!set) return {};
		if (role === '?') return {};
		let species = this.dex.species.get(set.species || set.name!);
		let stats = species.baseStats;

		let hasMove = this.hasMove;
		let moveCount = this.moveCount;

		let evs: StatsTable & {plusStat?: StatNameExceptHP | '', minusStat?: StatNameExceptHP | ''} = {
			hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0,
		};
		let plusStat: StatNameExceptHP | '' = '';
		let minusStat: StatNameExceptHP | '' = '';

		let statChart: {[role: string]: [StatName, StatName]} = {
			'Bulky Band': ['atk', 'hp'],
			'Fast Band': ['spe', 'atk'],
			'Bulky Specs': ['spa', 'hp'],
			'Fast Specs': ['spe', 'spa'],
			'Physical Scarf': ['spe', 'atk'],
			'Special Scarf': ['spe', 'spa'],
			'Physical Biased Mixed Scarf': ['spe', 'atk'],
			'Special Biased Mixed Scarf': ['spe', 'spa'],
			'Fast Physical Sweeper': ['spe', 'atk'],
			'Fast Special Sweeper': ['spe', 'spa'],
			'Bulky Physical Sweeper': ['atk', 'hp'],
			'Bulky Special Sweeper': ['spa', 'hp'],
			'Fast Bulky Support': ['spe', 'hp'],
			'Physically Defensive': ['def', 'hp'],
			'Specially Defensive': ['spd', 'hp'],
		};

		plusStat = statChart[role][0] as StatNameExceptHP;
		if (role === 'Fast Bulky Support') moveCount['Ultrafast'] = 0;
		if (plusStat === 'spe' && moveCount['Ultrafast']) {
			if (statChart[role][1] === 'atk' || statChart[role][1] === 'spa') {
				plusStat = statChart[role][1] as 'atk' | 'spa';
			} else if (moveCount['Physical'] >= 3) {
				plusStat = 'atk';
			} else if (stats.spd > stats.def) {
				plusStat = 'spd';
			} else {
				plusStat = 'def';
			}
		}

		if (this.supportsAVs) {
			// Let's Go, AVs enabled
			evs = {hp: 200, atk: 200, def: 200, spa: 200, spd: 200, spe: 200};
			if (!moveCount['PhysicalAttack']) evs.atk = 0;
			if (!moveCount['SpecialAttack']) evs.spa = 0;
			if (hasMove['gyroball'] || hasMove['trickroom']) evs.spe = 0;
		} else if (!this.supportsEVs) {
			// Let's Go, AVs disabled
			// no change
		} else if (this.ignoreEVLimits) {
			// Gen 1-2, hackable EVs (like Hackmons)
			evs = {hp: 252, atk: 252, def: 252, spa: 252, spd: 252, spe: 252};
			if (!moveCount['PhysicalAttack']) evs.atk = 0;
			if (!moveCount['SpecialAttack'] && this.dex.gen > 1) evs.spa = 0;
			if (hasMove['gyroball'] || hasMove['trickroom']) evs.spe = 0;
			if (this.dex.gen === 1) evs.spd = 0;
			if (this.dex.gen < 3) return evs;
		} else {
			// Normal Gen 3-7
			if (!statChart[role]) return {};

			let evTotal = 0;

			let primaryStat = statChart[role][0];
			let stat = this.getStat(primaryStat, set, 252, plusStat === primaryStat ? 1.1 : 1.0);
			let ev = 252;
			while (ev > 0 && stat <= this.getStat(primaryStat, set, ev - 4, plusStat === primaryStat ? 1.1 : 1.0)) ev -= 4;
			evs[primaryStat] = ev;
			evTotal += ev;

			let secondaryStat: StatName | null = statChart[role][1];
			if (secondaryStat === 'hp' && set.level && set.level < 20) secondaryStat = 'spd';
			stat = this.getStat(secondaryStat, set, 252, plusStat === secondaryStat ? 1.1 : 1.0);
			ev = 252;
			while (ev > 0 && stat <= this.getStat(secondaryStat, set, ev - 4, plusStat === secondaryStat ? 1.1 : 1.0)) ev -= 4;
			evs[secondaryStat] = ev;
			evTotal += ev;

			let SRweaknesses = ['Fire', 'Flying', 'Bug', 'Ice'];
			let SRresistances = ['Ground', 'Steel', 'Fighting'];
			let SRweak = 0;
			if (set.ability !== 'Magic Guard' && set.ability !== 'Mountaineer') {
				if (SRweaknesses.indexOf(species.types[0]) >= 0) {
					SRweak++;
				} else if (SRresistances.indexOf(species.types[0]) >= 0) {
					SRweak--;
				}
				if (SRweaknesses.indexOf(species.types[1]) >= 0) {
					SRweak++;
				} else if (SRresistances.indexOf(species.types[1]) >= 0) {
					SRweak--;
				}
			}
			let hpDivisibility = 0;
			let hpShouldBeDivisible = false;
			let hp = evs['hp'] || 0;
			stat = this.getStat('hp', set, hp, 1);
			if ((set.item === 'Leftovers' || set.item === 'Black Sludge') && hasMove['substitute'] && stat !== 404) {
				hpDivisibility = 4;
			} else if (set.item === 'Leftovers' || set.item === 'Black Sludge') {
				hpDivisibility = 0;
			} else if (hasMove['bellydrum'] && (set.item || '').slice(-5) === 'Berry') {
				hpDivisibility = 2;
				hpShouldBeDivisible = true;
			} else if (hasMove['substitute'] && (set.item || '').slice(-5) === 'Berry') {
				hpDivisibility = 4;
				hpShouldBeDivisible = true;
			} else if (SRweak >= 2 || hasMove['bellydrum']) {
				hpDivisibility = 2;
			} else if (SRweak >= 1 || hasMove['substitute'] || hasMove['transform']) {
				hpDivisibility = 4;
			} else if (set.ability !== 'Magic Guard') {
				hpDivisibility = 8;
			}

			if (hpDivisibility) {
				while (hp < 252 && evTotal < 508 && !(stat % hpDivisibility) !== hpShouldBeDivisible) {
					hp += 4;
					stat = this.getStat('hp', set, hp, 1);
					evTotal += 4;
				}
				while (hp > 0 && !(stat % hpDivisibility) !== hpShouldBeDivisible) {
					hp -= 4;
					stat = this.getStat('hp', set, hp, 1);
					evTotal -= 4;
				}
				while (hp > 0 && stat === this.getStat('hp', set, hp - 4, 1)) {
					hp -= 4;
					evTotal -= 4;
				}
				if (hp || evs['hp']) evs['hp'] = hp;
			}

			if (species.id === 'tentacruel') {
				evTotal = this.ensureMinEVs(evs, 'spe', 16, evTotal);
			} else if (species.id === 'skarmory') {
				evTotal = this.ensureMinEVs(evs, 'spe', 24, evTotal);
			} else if (species.id === 'jirachi') {
				evTotal = this.ensureMinEVs(evs, 'spe', 32, evTotal);
			} else if (species.id === 'celebi') {
				evTotal = this.ensureMinEVs(evs, 'spe', 36, evTotal);
			} else if (species.id === 'volcarona') {
				evTotal = this.ensureMinEVs(evs, 'spe', 52, evTotal);
			} else if (species.id === 'gliscor') {
				evTotal = this.ensureMinEVs(evs, 'spe', 72, evTotal);
			} else if (species.id === 'dragonite' && evs['hp']) {
				evTotal = this.ensureMaxEVs(evs, 'spe', 220, evTotal);
			}

			if (evTotal < 508) {
				let remaining = 508 - evTotal;
				if (remaining > 252) remaining = 252;
				secondaryStat = null;
				if (!evs['atk'] && moveCount['PhysicalAttack'] >= 1) {
					secondaryStat = 'atk';
				} else if (!evs['spa'] && moveCount['SpecialAttack'] >= 1) {
					secondaryStat = 'spa';
				} else if (stats.hp === 1 && !evs['def']) {
					secondaryStat = 'def';
				} else if (stats.def === stats.spd && !evs['spd']) {
					secondaryStat = 'spd';
				} else if (!evs['spd']) {
					secondaryStat = 'spd';
				} else if (!evs['def']) {
					secondaryStat = 'def';
				}
				if (secondaryStat) {
					ev = remaining;
					stat = this.getStat(secondaryStat, set, ev);
					while (ev > 0 && stat === this.getStat(secondaryStat, set, ev - 4)) ev -= 4;
					if (ev) evs[secondaryStat] = ev;
					remaining -= ev;
				}
				if (remaining && !evs['spe']) {
					ev = remaining;
					stat = this.getStat('spe', set, ev);
					while (ev > 0 && stat === this.getStat('spe', set, ev - 4)) ev -= 4;
					if (ev) evs['spe'] = ev;
				}
			}

		}

		if (hasMove['gyroball'] || hasMove['trickroom']) {
			minusStat = 'spe';
		} else if (!moveCount['PhysicalAttack']) {
			minusStat = 'atk';
		} else if (moveCount['SpecialAttack'] < 1 && !evs['spa']) {
			if (moveCount['SpecialAttack'] < moveCount['PhysicalAttack']) {
				minusStat = 'spa';
			} else if (!evs['atk']) {
				minusStat = 'atk';
			}
		} else if (moveCount['PhysicalAttack'] < 1 && !evs['atk']) {
			minusStat = 'atk';
		} else if (stats.def > stats.spe && stats.spd > stats.spe && !evs['spe']) {
			minusStat = 'spe';
		} else if (stats.def > stats.spd) {
			minusStat = 'spd';
		} else {
			minusStat = 'def';
		}

		if (plusStat === minusStat) {
			minusStat = (plusStat === 'spe' ? 'spd' : 'spe');
		}

		evs.plusStat = plusStat;
		evs.minusStat = minusStat;

		return evs;
	}

	getStat(stat: StatName, set: PokemonSet, evOverride?: number, natureOverride?: number) {
		let species = this.dex.species.get(set.species);
		if (!species.exists) return 0;

		let level = set.level || 100;

		let baseStat = species.baseStats[stat];

		let iv = (set.ivs && set.ivs[stat]);
		if (typeof iv !== 'number') iv = 31;
		if (this.dex.gen <= 2) iv &= 30;

		let ev = (set.evs && set.evs[stat]);
		if (typeof ev !== 'number') ev = (this.dex.gen > 2 ? 0 : 252);
		if (evOverride !== undefined) ev = evOverride;

		if (stat === 'hp') {
			if (baseStat === 1) return 1;
			if (!this.supportsEVs) return ~~(~~(2 * baseStat + iv + 100) * level / 100 + 10) + (this.supportsAVs ? ev : 0);
			return ~~(~~(2 * baseStat + iv + ~~(ev / 4) + 100) * level / 100 + 10);
		}
		let val = ~~(~~(2 * baseStat + iv + ~~(ev / 4)) * level / 100 + 5);
		if (!this.supportsEVs) {
			val = ~~(~~(2 * baseStat + iv) * level / 100 + 5);
		}
		if (natureOverride) {
			val *= natureOverride;
		} else if (BattleNatures[set.nature!]?.plus === stat) {
			val *= 1.1;
		} else if (BattleNatures[set.nature!]?.minus === stat) {
			val *= 0.9;
		}
		if (!this.supportsEVs) {
			let friendshipValue = ~~((70 / 255 / 10 + 1) * 100);
			val = ~~(val) * friendshipValue / 100 + (this.supportsAVs ? ev : 0);
		}
		return ~~(val);
	}
}

if (typeof require === 'function') {
	// in Node
	(global as any).BattleStatGuesser = BattleStatGuesser;
}

/** Find a legal spread that preserves every stat while saving EVs. */
function BattleStatOptimizer(set: PokemonSet, format: ID) {
	const calculator = new BattleStatGuesser(format);
	if (calculator.dex.gen < 3 || !calculator.supportsEVs || calculator.ignoreEVLimits) return null;
	const originalTotal = Dex.statNames.reduce((sum, stat) => sum + (set.evs?.[stat] || 0), 0);
	if (originalTotal > 510) return null;
	const targets = Dex.statNames.map(stat => calculator.getStat(stat, set));
	let best: {evs: StatsTable; plus?: StatNameExceptHP; minus?: StatNameExceptHP; savedEVs: number} | null = null;
	for (const nature of Object.values(BattleNatures)) {
		const evs: StatsTable = {hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0};
		let possible = true;
		for (let i = 0; i < Dex.statNames.length; i++) {
			const stat = Dex.statNames[i];
			const multiplier = nature.plus === stat ? 1.1 : nature.minus === stat ? 0.9 : 1;
			let ev = 0;
			while (ev <= 252 && calculator.getStat(stat, set, ev, multiplier) < targets[i]) ev += 4;
			if (ev > 252) { possible = false; break; }
			evs[stat] = ev;
		}
		const savedEVs = originalTotal - Object.values(evs).reduce((a, b) => a + b, 0);
		if (possible && savedEVs > (best?.savedEVs || 0)) best = {evs, plus: nature.plus, minus: nature.minus, savedEVs};
	}
	return best;
}
if (typeof require === 'function') (global as any).BattleStatOptimizer = BattleStatOptimizer;
